/*
 * Interactive Avatar Panel V2
 * ---------------------------------------------------------------------------
 * The client-facing surface for the live avatar: video on a stage, a live
 * transcript, and a composer that takes speech *and* typed text in the same
 * session. Two placements share this one component:
 *
 *   drawer     - a full-height panel pinned to the side of the page
 *   spotlight  - a command pill at the bottom that grows into a centered card
 *
 * Neither dims or blurs the host page. The panel only ever occupies its own
 * rectangle, so the visitor can keep reading, scrolling and clicking while the
 * conversation runs.
 *
 * Minimizing does not end the session. The whole component stays mounted and
 * only its geometry changes, so the LiveKit room, the avatar video track and
 * the transcript survive a collapse back to the launcher pill.
 *
 * Picture in picture is the same trick taken one step further: the chrome goes
 * away entirely and the stage becomes a free-floating video card the visitor
 * can drag anywhere on the page, still on the same session (see `mode === 'pip'`
 * and the drag protocol below).
 *
 * Layout is fully absolute and driven by measured numbers (see `geometry`)
 * rather than flow, which is what lets the stage morph between the panel band,
 * the mini pill's circle and the floating PIP card on a single transition.
 *
 * Theme comes from `agent.avatar_panel_config` (one JSON blob, written by
 * AvatarPanelThemeCustomizer) with URL params overriding it for previews.
 * Every colour in the UI is derived from four inputs, so a client only has to
 * pick a background, a surface, an accent and a launcher colour to have the
 * panel match their site - including switching to a light-on-dark or
 * dark-on-light text ramp automatically.
 */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Room, RoomEvent, VideoQuality } from 'livekit-client';
import axios from 'axios';
import {
  Mic, MicOff, Send, X, ChevronDown, PhoneOff, Loader2, MessageSquare, Sparkles, Volume2,
  Clock, ArrowLeft, Trash2, RotateCcw, PictureInPicture2, Maximize2, MousePointerClick, ImagePlus,
} from 'lucide-react';
import useCobrowseBridge, { waitForParticipant } from './useCobrowseBridge';
import { foldCallerTurn } from './transcriptFold';
import {
  CHAT_IMAGE_ACCEPT,
  CHAT_IMAGE_TOPIC,
  useChatImageAttachments,
  imageFilesFrom,
  ChatAttachmentStrip,
  ChatMessageImages,
  chatImagesFromIds,
  imageTranscriptText,
  stripImageMarker,
  modelSupportsImages,
  useImageDrop,
  ChatDropOverlay,
} from './chatImageAttachments';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: false,
  headers: { 'Content-Type': 'application/json' },
});

// ─── LiveKit wiring ───────────────────────────────────────────────────────────

// The voice agent's RoomIO registers a text-stream handler on this topic and
// treats an incoming message as a user turn (interrupt + generate_reply), so a
// typed reply runs the full agent - knowledge base, custom functions and all -
// and comes back spoken through the avatar.
const CHAT_TOPIC = 'lk.chat';

// Both sides of the conversation stream here as they happen: agent text while
// the LLM produces it, visitor text from STT (interim, then final). The
// backend's own data-channel transcript only fires once a turn is committed to
// history, which with an avatar lands a full turn late.
const TRANSCRIPTION_TOPIC = 'lk.transcription';
const SEGMENT_ID_ATTR = 'lk.segment_id';

const AVATAR_IDENTITY = 'liveavatar-avatar-agent';
const LOCAL_IDENTITY = 'liveavatar-user'; // what the backend watches for to activate the avatar

// ─── Co-browse ────────────────────────────────────────────────────────────────
// With ?cobrowse=1 the agent can also scroll, navigate and fill forms on the host
// page, and the call carries on across full page loads (useCobrowseBridge.js).
// A page change waits for the avatar to finish speaking: a short grace for the
// speech to start, then this much silence with nothing left to reveal.
const COBROWSE_SPEECH_START_GRACE_MS = 700;
const COBROWSE_SPEECH_QUIET_MS = 450;
// Transcript lines kept in the host page's call record for the next page
const COBROWSE_SAVED_CAPTIONS = 60;
const COBROWSE_SAVE_INTERVAL_MS = 1000;

// ─── Loader protocol ──────────────────────────────────────────────────────────
// public/avatar-panel-v2.js owns placement on the host page: it knows the real
// viewport, this document does not. The panel reports what it is doing and how
// big its content-sized states are; the loader turns that into an iframe rect.
const MSG_STATE = 'braincx-avatar-panel-v2-state';
const MSG_MEASURE = 'braincx-avatar-panel-v2-measure';
const MSG_READY = 'braincx-avatar-panel-v2-ready';
const MSG_COMMAND = 'braincx-avatar-panel-v2-command';
const MSG_VIEWPORT = 'braincx-avatar-panel-v2-viewport';
const MSG_INSET = 'braincx-avatar-panel-v2-inset';
// Dragging the PIP card moves the *iframe*, which only the loader can do. The
// pointer is inside this document, so the panel forwards it: `start` carries
// where in the card it was grabbed, `move` carries the pointer in this
// document's coordinates, and the loader turns the pair into a position.
const MSG_DRAG = 'braincx-avatar-panel-v2-drag';

// ─── Caption pacing ───────────────────────────────────────────────────────────
// LiveKit's transcript synchronizer is disabled in an avatar session (the
// avatar replaces audio output after the session starts), so agent text arrives
// as fast as the LLM writes it. The panel holds it back and reveals it in step
// with the speech actually playing.
const REVEAL_TICK_MS = 80;
// A background tab throttles setInterval to about once a second. Clamping the
// gap tighter than that loses speech the avatar really did play, so the text
// falls further behind every tick the tab is hidden and lands out of step the
// moment it comes back.
const MAX_TICK_MS = 1200;
const DEFAULT_CHARS_PER_SEC = 15; // ~150 words per minute
const MIN_CHARS_PER_SEC = 8;
const MAX_CHARS_PER_SEC = 32;
const VOICE_RMS_THRESHOLD = 0.01;
// A voice the browser mixes down never clears a fixed threshold, and the reveal
// would then wait out the whole turn on a track that is plainly talking. When
// the loudest thing recently heard sits under the threshold but above the noise
// floor, the gate drops to a fraction of it instead.
const QUIET_TRACK_FLOOR = 0.0015;
const QUIET_GATE_RATIO = 0.4;
const PEAK_DECAY_PER_TICK = 0.972; // ~2s half-life: the gate follows this turn
const VOICE_HANGOVER_MS = 400; // bridge the pauses between words
const END_OF_SPEECH_SILENCE_MS = 900; // silence after the text ended closes the turn
const NO_AUDIO_FALLBACK_MS = 4000; // no measurable audio: reveal anyway rather than stall
// A text stream that never closed (the turn errored out) must not sit at the
// head of the queue holding every later turn behind it.
const ORPHAN_SEGMENT_MS = 2500;
// A visitor turn starting while the avatar is mid-sentence is a barge-in: the
// agent stops there, so the rest of the reply is never spoken and must never be
// written. The window around the moment the avatar fell silent is deliberately
// lopsided. Talking over the avatar reaches this document at or shortly before
// that moment, because the interrupt fires off the same speech; answering a
// question the moment it finished reaches it afterwards, and is not a barge-in.
const BARGE_IN_LEAD_MS = 2000;
const BARGE_IN_LAG_MS = 600;
const CUT_MARKER = '\u2026';
const CUT_MEMORY = 4; // recent cut turns remembered, so a late commit cannot undo one
// A reply cancelled before a word of it played is dropped once the next reply
// arrives, or this long after the visitor started talking over it if none does
const UNSPOKEN_REPLY_MS = 12000;
// How long a streamed bubble can still be taken over by another copy of the same
// utterance. LiveKit republishes one utterance under a fresh segment id whenever
// its transcription output is not mid-capture, so the same words arrive on a key
// this panel has never seen and land as a second bubble. Prefix matching alone
// would fix that and cause a worse bug: across a whole conversation it would fold
// a later "yes" into an earlier "yes please". A few seconds is longer than any
// interim-to-final gap and far shorter than a turn worth confusing it with.
const SAME_UTTERANCE_MS = 4000;

// ─── Colour helpers ───────────────────────────────────────────────────────────

const clamp01 = (n) => Math.min(1, Math.max(0, n));

const parseHex = (hex) => {
  let h = String(hex || '').trim().replace('#', '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (!/^[0-9a-f]{6}$/i.test(h)) return null;
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
};

const toHex = (rgb) =>
  '#' + rgb.map((c) => Math.round(clamp01(c / 255) * 255).toString(16).padStart(2, '0')).join('');

const rgba = (hex, alpha) => {
  const rgb = parseHex(hex) || [0, 0, 0];
  return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${alpha})`;
};

/** Relative luminance, used to decide the whole text ramp from one background. */
const luminance = (hex) => {
  const rgb = parseHex(hex) || [0, 0, 0];
  const [r, g, b] = rgb.map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

const mix = (a, b, t) => {
  const A = parseHex(a) || [0, 0, 0];
  const B = parseHex(b) || [255, 255, 255];
  return toHex(A.map((c, i) => c + (B[i] - c) * clamp01(t)));
};

/** Black or white, whichever stays legible on `hex`. */
const contrastOn = (hex) => (luminance(hex) > 0.45 ? '#0b1017' : '#ffffff');

// ─── Settings ─────────────────────────────────────────────────────────────────

const SYSTEM_FONT =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

// How far a shadow may travel before the iframe edge clips it into a visible
// grey rectangle. The loader keeps a transparent gutter of this size around the
// widget (SHADOW_GUTTER in avatar-panel-v2.js) and reports what it managed to
// fit on each side; every shadow below is sized to fade to nothing within it.
const SHADOW_REACH = 20;

const NO_INSET = { l: 0, t: 0, r: 0, b: 0 };

// Mirrors MOBILE_MAX in avatar-panel-v2.js. Below this the loader ignores the
// spotlight's width, anchor and layout entirely and draws a full width bottom
// sheet, so the panel has to agree with it about where "a phone" starts.
const MOBILE_MAX = 560;

const LAUNCHER_SIZES = {
  small: { dot: 46, pillH: 48, font: 13, logo: 30 },
  medium: { dot: 58, pillH: 56, font: 14, logo: 36 },
  large: { dot: 68, pillH: 64, font: 15, logo: 42 },
};

export const AVATAR_PANEL_DEFAULTS = {
  // placement
  variant: 'drawer', // 'drawer' | 'spotlight'
  side: 'right', // drawer edge
  drawerEdge: 'floating', // 'floating' (inset, rounded) | 'flush' (against the edge)
  position: 'bottom-right', // launcher corner; spotlight also accepts 'bottom-center'
  panelWidth: 420, // drawer width
  spotlightWidth: 860,
  spotlightHeight: 620,
  // How the spotlight card is sized. 'fixed' is the two numbers above, which is
  // right for a design signed off against one screen and wrong on every other
  // one: 860x620 is a modest card on a 27" monitor and most of a 13" laptop.
  // 'scale' sizes the card as a share of the visitor's own viewport instead,
  // and the two numbers above become the ceiling rather than the target - so
  // the card covers the same proportion of both screens and still never grows
  // past what the client designed for.
  spotlightSizeMode: 'fixed', // 'fixed' | 'scale'
  spotlightScale: 62, // % of the viewport, used when spotlightSizeMode is 'scale'
  // Where the card sits. Centered is the loudest placement there is: it lands on
  // the middle of whatever the visitor was reading. Anchoring it to an edge
  // leaves the page's own content uncovered without making the card smaller.
  spotlightAnchor: 'center', // 'center' | 'bottom' | 'bottom-right' | 'bottom-left' | 'top'
  // Two ways to lay a wide spotlight out. 'split' is the two columns - avatar
  // on the left, conversation on the right - and is what a wide card has always
  // done. 'stacked' puts the video across the top with the conversation under
  // it, which is the shape people already know from a video call and reads
  // better when the avatar is being watched rather than glanced at.
  spotlightLayout: 'split', // 'split' | 'stacked'
  // Share of the card's height the video takes when stacked. Ignored by the
  // split layout, where the video is a full-height column instead.
  spotlightStageRatio: 45,
  radius: 20,

  // colour
  bgColor: '#0c1220',
  surfaceColor: '#161f31',
  accentColor: '#5b8cff',
  launcherColor: '', // falls back to accent
  textColor: '', // '' derives from the background
  fontFamily: '',

  // launcher
  launcherStyle: 'pill', // 'pill' | 'circle'
  launcherSize: 'medium',
  launcherLabel: 'Talk to us',
  launcherIcon: 'sparkles', // 'sparkles' | 'chat' | 'mic'
  showLauncherPreview: true, // show the avatar poster inside the launcher
  pulse: true,
  teaserEnabled: true,
  teaserMessage: 'Hi. I am live right now, ask me anything.',
  teaserDelay: 5000,

  // copy
  title: '', // falls back to the agent name
  subtitle: 'Live AI assistant',
  headline: 'Talk face to face',
  description: 'Speak or type. The assistant answers out loud and in writing, live.',
  startLabel: 'Start conversation',
  inputPlaceholder: 'Type a message',
  consentText: 'Your microphone is only used while the conversation is running.',
  poweredBy: true,

  // behaviour
  posterUrl: '',
  captionStyle: 'transcript', // 'transcript' | 'subtitles'
  showTextInput: true,
  showMicToggle: true,
  startMuted: false,
  autoStart: false,
  showTimer: true,
  minimizeEnabled: true,
  mobileMode: 'sheet', // 'sheet' keeps part of the page visible; 'full' takes the screen

  // Voice and text with no face. Off by default, because the panel is sold on
  // the avatar. Turned on, the same session runs unchanged - microphone, typed
  // messages, transcript, history - with no video stage, no pop out, and no
  // LiveAvatar session started on the backend at all. The only thing missing is
  // the avatar.
  hideAvatar: false,

  // picture in picture
  pipEnabled: true,
  pipWidth: 300,
  pipAspect: 'landscape', // 'landscape' 16:9 | 'portrait' 3:4 | 'square'

  // history
  // Off by default, and deliberately so. Turning it on means keeping a
  // visitor's transcript for a day, which is the client's decision to make
  // rather than one the platform makes for them the moment they embed a panel.
  historyEnabled: false,
  historyLabel: 'History',
};

const BOOL_PARAMS = [
  'pulse', 'teaserEnabled', 'showLauncherPreview', 'showTextInput', 'showMicToggle',
  'startMuted', 'autoStart', 'showTimer', 'minimizeEnabled', 'poweredBy',
  'historyEnabled', 'pipEnabled', 'hideAvatar',
];
const NUM_PARAMS = [
  'panelWidth', 'spotlightWidth', 'spotlightHeight', 'spotlightScale',
  'spotlightStageRatio', 'radius', 'teaserDelay', 'pipWidth',
];
const HEX_PARAMS = {
  bg: 'bgColor', surface: 'surfaceColor', accent: 'accentColor',
  launcher: 'launcherColor', text: 'textColor',
};
const STR_PARAMS = [
  'variant', 'side', 'drawerEdge', 'position', 'launcherStyle', 'launcherSize',
  'launcherLabel', 'launcherIcon', 'title', 'subtitle', 'headline', 'description',
  'startLabel', 'inputPlaceholder', 'consentText', 'teaserMessage', 'captionStyle',
  'posterUrl', 'fontFamily', 'mobileMode', 'historyLabel', 'pipAspect',
  'spotlightSizeMode', 'spotlightAnchor', 'spotlightLayout',
];

// The floating PIP card's shape. The video is cover-cropped into it, exactly as
// it is on the panel stage, so a portrait avatar in a landscape card is framed
// rather than letterboxed.
const PIP_ASPECTS = { landscape: 16 / 9, portrait: 3 / 4, square: 1 };

/**
 * Size of the floating card: the client's chosen width at the chosen aspect,
 * shrunk to fit when the viewport cannot hold it. Measured against the *host*
 * viewport, which under the loader is not this iframe's - in PIP the iframe is
 * only as big as the card, so sizing the card from it would shrink on itself.
 *
 * Exported because the loader needs the same two numbers to size the iframe,
 * and it gets them from the panel over `MSG_STATE` rather than recomputing.
 */
export const avatarPipSize = (settings, viewportW, viewportH) => {
  const ratio = PIP_ASPECTS[settings.pipAspect] || PIP_ASPECTS.landscape;
  const vw = viewportW || 1280;
  // A desktop-sized card on a phone is not a picture in picture, it is a panel
  // with no controls. Capped so the page it floats over stays usable.
  const maxW = vw < 560 ? Math.max(140, Math.min(vw - 24, 240)) : Math.max(140, vw - 32);
  const maxH = Math.max(100, (viewportH || 800) - 32);
  let width = Math.min(Math.max(160, Math.round(settings.pipWidth) || 300), maxW);
  let height = Math.round(width / ratio);
  if (height > maxH) {
    height = maxH;
    width = Math.round(height * ratio);
  }
  return { width, height };
};

// ─── Spotlight sizing ────────────────────────────────────────────────────────

const clampNum = (value, min, max, fallback) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, Math.round(n)));
};

/**
 * Size of the spotlight card, in one place.
 *
 * Measured against the *host* viewport for the same reason the PIP card is:
 * under the loader this iframe is only as big as the widget, so sizing the
 * widget from it would be circular.
 *
 * Exported because the loader needs the same two numbers to size the iframe,
 * and - exactly as with `avatarPipSize` - it gets them from the panel over
 * `MSG_STATE` rather than recomputing them. Placement is still the loader's:
 * it turns this size plus `spotlightAnchor` into a rect on the page.
 */
export const avatarSpotlightSize = (settings, viewportW, viewportH) => {
  const vw = viewportW || 1280;
  const vh = viewportH || 800;
  // The loader keeps a 20px shadow gutter on each side and squeezing it stamps
  // a grey band into the corners, so the card never asks for those 40px.
  const maxW = Math.max(280, vw - 40);
  const maxH = Math.max(320, vh - 40);
  const capW = clampNum(settings.spotlightWidth, 360, 1600, 860);
  const capH = clampNum(settings.spotlightHeight, 360, 1200, 620);

  let width;
  let height;
  if (settings.spotlightSizeMode === 'scale') {
    const pct = clampNum(settings.spotlightScale, 30, 100, 62) / 100;
    // Both axes read the viewport, so the card keeps the screen's own shape and
    // a wide monitor gets a wide card rather than a tall one with empty sides.
    width = Math.min(Math.round(vw * pct), capW);
    height = Math.min(Math.round(vh * pct), capH);
  } else {
    width = capW;
    height = capH;
  }

  return {
    width: Math.max(300, Math.min(width, maxW)),
    height: Math.max(340, Math.min(height, maxH)),
  };
};

/**
 * Top-left of the card for a given anchor, clamped so it always lands inside
 * the viewport with room for the gutter. `center` is the historical behaviour
 * and stays the default.
 */
export const avatarSpotlightRect = (settings, viewportW, viewportH) => {
  const vw = viewportW || 1280;
  const vh = viewportH || 800;
  const box = avatarSpotlightSize(settings, vw, vh);
  const gap = 24;
  const anchor = settings.spotlightAnchor || 'center';
  const centerX = Math.round((vw - box.width) / 2);
  const centerY = Math.round((vh - box.height) / 2);
  let left = centerX;
  let top = centerY;
  if (anchor === 'bottom') top = vh - box.height - gap;
  else if (anchor === 'top') top = gap;
  else if (anchor === 'bottom-right') { left = vw - box.width - gap; top = vh - box.height - gap; }
  else if (anchor === 'bottom-left') { left = gap; top = vh - box.height - gap; }
  return {
    left: Math.max(0, Math.min(left, Math.max(0, vw - box.width))),
    top: Math.max(0, Math.min(top, Math.max(0, vh - box.height))),
    width: box.width,
    height: box.height,
  };
};

// The panel header, as a module constant so the sizing helpers below and the
// component's own geometry cannot drift apart.
const PANEL_HEADER_H = 62;

/**
 * Height of the video in a stacked spotlight.
 *
 * The client's chosen share of the card, with one hard floor under it: the
 * conversation keeps 180px whatever happens, because a transcript and a
 * composer squeezed below that stop being usable and the send button starts
 * falling off the bottom. So a short card quietly gives the video less than the
 * slider asks for - which is why the customizer prints the real number back.
 */
export const SPOTLIGHT_BODY_MIN = 180;

export const avatarSpotlightStageHeight = (settings, cardHeight) => {
  const h = Math.max(320, cardHeight || 620);
  const ratio = clampNum(settings.spotlightStageRatio, 25, 60, 45) / 100;
  return Math.max(150, Math.min(Math.round(h * ratio), h - PANEL_HEADER_H - SPOTLIGHT_BODY_MIN));
};

/**
 * Saved config first, URL params on top. Params exist so the customizer's
 * Preview button can show unsaved edits, and so a client can override one
 * thing (say the side) per page without touching the agent.
 */
export const getAvatarPanelSettings = (agent, urlParams) => {
  const p = urlParams || new URLSearchParams();
  const saved = (agent && agent.avatar_panel_config) || {};
  const s = { ...AVATAR_PANEL_DEFAULTS, ...saved };

  STR_PARAMS.forEach((key) => {
    const v = p.get(key);
    if (v !== null && v !== '') s[key] = v;
  });
  NUM_PARAMS.forEach((key) => {
    const v = parseInt(p.get(key), 10);
    if (Number.isFinite(v)) s[key] = v;
  });
  BOOL_PARAMS.forEach((key) => {
    const v = p.get(key);
    if (v === '1' || v === 'true') s[key] = true;
    else if (v === '0' || v === 'false') s[key] = false;
  });
  Object.keys(HEX_PARAMS).forEach((param) => {
    const v = p.get(param);
    if (v) s[HEX_PARAMS[param]] = '#' + v.replace('#', '');
  });

  s.logoUrl = (agent && agent.avatar_panel_logo_url) || null;
  s.title = s.title || (agent && agent.name) || 'Assistant';
  return s;
};

/**
 * Turn a saved config into the query string the panel and the loader accept.
 * Used by the customizer's Preview (which must show unsaved edits) and by the
 * embed modal when a client wants the snippet to carry its own overrides.
 * Values equal to the default are dropped so the URL stays short.
 */
export const avatarPanelParams = (config) => {
  const p = new URLSearchParams();
  const c = { ...AVATAR_PANEL_DEFAULTS, ...(config || {}) };
  STR_PARAMS.forEach((key) => {
    if (c[key] && c[key] !== AVATAR_PANEL_DEFAULTS[key]) p.set(key, c[key]);
  });
  NUM_PARAMS.forEach((key) => {
    if (Number.isFinite(c[key]) && c[key] !== AVATAR_PANEL_DEFAULTS[key]) p.set(key, String(c[key]));
  });
  BOOL_PARAMS.forEach((key) => {
    if (!!c[key] !== !!AVATAR_PANEL_DEFAULTS[key]) p.set(key, c[key] ? '1' : '0');
  });
  Object.keys(HEX_PARAMS).forEach((param) => {
    const value = c[HEX_PARAMS[param]];
    if (value && value !== AVATAR_PANEL_DEFAULTS[HEX_PARAMS[param]]) {
      p.set(param, String(value).replace('#', ''));
    }
  });
  return p;
};

/**
 * Four client-chosen colours expand into the full ramp the UI needs. Deriving
 * rather than exposing twenty pickers is what keeps a custom palette coherent:
 * pick a cream background and the panel becomes a light theme, borders and
 * muted text included, with no extra input.
 */
export const buildAvatarPanelTheme = (s) => {
  const bg = parseHex(s.bgColor) ? s.bgColor : AVATAR_PANEL_DEFAULTS.bgColor;
  const isLight = luminance(bg) > 0.5;
  const text = parseHex(s.textColor) ? s.textColor : (isLight ? '#0d1420' : '#f3f6fb');
  const surface = parseHex(s.surfaceColor)
    ? s.surfaceColor
    : mix(bg, text, isLight ? 0.05 : 0.08);
  const accent = parseHex(s.accentColor) ? s.accentColor : AVATAR_PANEL_DEFAULTS.accentColor;
  const launcher = parseHex(s.launcherColor) ? s.launcherColor : accent;

  return {
    isLight,
    bg,
    surface,
    elevated: mix(surface, text, isLight ? 0.05 : 0.07),
    text,
    muted: rgba(text, 0.62),
    faint: rgba(text, 0.42),
    border: rgba(text, isLight ? 0.12 : 0.14),
    hairline: rgba(text, isLight ? 0.07 : 0.09),
    accent,
    accentText: contrastOn(accent),
    accentSoft: rgba(accent, isLight ? 0.12 : 0.18),
    launcher,
    launcherText: contrastOn(launcher),
    danger: isLight ? '#c0392b' : '#ff6b6b',
    dangerSoft: isLight ? 'rgba(192,57,43,0.10)' : 'rgba(255,107,107,0.16)',
    live: '#31d0aa',
    radius: Math.max(0, Math.min(32, s.radius ?? 20)),
    font: s.fontFamily ? `${s.fontFamily}, ${SYSTEM_FONT}` : SYSTEM_FONT,
    // Sized to die out within SHADOW_REACH: 5px down + a 14px blur reaches 19px,
    // so nothing lands on the iframe edge to be clipped into a grey band.
    shadow: isLight
      ? '0 5px 14px rgba(15, 25, 45, 0.13), 0 1px 3px rgba(15, 25, 45, 0.09)'
      : '0 5px 14px rgba(0, 0, 0, 0.42), 0 1px 3px rgba(0, 0, 0, 0.30)',
  };
};

// ─── Transcript helpers (carried over from the V1 panel, proven in session) ──

const normalizeText = (text) => (text || '').replace(/\s+/g, ' ').trim();

// Compare on letters and digits only. An interim transcript arrives without the
// casing and punctuation the final one gets, so raw text will not line up while
// a turn is still being spoken.
const compareKey = (text) => {
  const stripped = (text || '').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
  return stripped || normalizeText(text).toLowerCase();
};

const isSameTurn = (caption, speaker, text) => {
  if (caption.speaker !== speaker) return false;
  const a = compareKey(caption.text);
  const b = compareKey(text);
  return a === b || a.startsWith(b) || b.startsWith(a);
};

const isStreamedKey = (key) => typeof key === 'string' && key.startsWith('segment-');
const longerOf = (a, b) => (b.length >= a.length ? b : a);
// A turn the visitor cut off can only ever shrink: every copy of it that arrives
// later was written by the LLM, not spoken by the avatar.
const shorterOf = (a, b) => (b.length <= a.length ? b : a);

/**
 * Index of the first caption belonging to the *current* session.
 *
 * Resuming seeds the transcript with the earlier thread, and those turns sit at
 * the head of the array with `earlier: true`. Every dedupe, merge and adopt
 * window below must stop here. A turn spoken now that happens to look like one
 * spoken an hour ago is a real, separate turn: folding the two together
 * rewrites the earlier bubble in place, drags the new words into the middle of
 * the old thread, and - because the panel flushes exactly what is on screen -
 * writes that scrambled version back to storage, where the next resume seeds
 * from it and the webhook reads it as `previous_transcript`. Prefix matching
 * makes it easy to hit: a resumed agent opens by referring back to what was
 * said before, so its first few characters routinely match the tail it is
 * referring to.
 */
const liveStart = (list) => {
  let i = list.length;
  while (i > 0 && !list[i - 1].earlier) i -= 1;
  return i;
};

const revealedText = (full, count) => {
  if (count >= full.length) return full;
  const slice = full.slice(0, Math.max(0, Math.floor(count)));
  const boundary = slice.lastIndexOf(' ');
  return boundary > 0 ? slice.slice(0, boundary) : '';
};

/**
 * Clear everything the reveal loop carries between turns. The learned speaking
 * rate is deliberately kept: it belongs to this agent's voice, not to a session.
 */
const resetRevealState = (state) => {
  state.segments = [];
  state.lastVoiceAt = 0;
  state.lastTickAt = 0;
  state.peakLevel = 0;
  state.carryVoice = false;
  state.bargeInAt = 0;
  state.cutTurns = [];
};

/**
 * Learn this voice's real speaking rate from a turn that was heard end to end.
 *
 * A turn that was cut off, or that ran blind because nothing measurable came off
 * the track, says nothing about how fast the avatar speaks: a barge-in three
 * words into a long answer looks like an impossibly fast talker, and feeding
 * that in is what leaves the *next* few turns visibly out of step.
 */
const learnSpeakingRate = (state, segment) => {
  if (segment.blindMs > 0) return;
  if (segment.spokenMs <= 1000 || segment.full.length <= 20) return;
  const measured = (segment.full.length / segment.spokenMs) * 1000;
  state.charsPerSec = Math.min(
    MAX_CHARS_PER_SEC,
    Math.max(MIN_CHARS_PER_SEC, state.charsPerSec * 0.7 + measured * 0.3)
  );
};

const withCutMarker = (text) => (text.endsWith(CUT_MARKER) ? text : text + CUT_MARKER);

// Still being written, so another copy of the same words is the same utterance
// rather than a new turn that happens to start the same way. `updatedAt` moves
// with every chunk, which matters for a long utterance whose first interim landed
// ten seconds before its final.
const stillOpen = (caption, now) => now - (caption.updatedAt || caption.at || 0) < SAME_UTTERANCE_MS;

const isAvatarParticipant = (participant) =>
  participant.identity === AVATAR_IDENTITY ||
  participant.name === AVATAR_IDENTITY ||
  (participant.identity && participant.identity.includes('liveavatar')) ||
  (participant.name && participant.name.includes('liveavatar'));

const formatClock = (seconds) => {
  const m = Math.floor(seconds / 60);
  const sec = seconds % 60;
  return `${m}:${String(sec).padStart(2, '0')}`;
};

// ─── History ──────────────────────────────────────────────────────────────────
// The panel keeps the transcript it is showing on the server for 24 hours so the
// same visitor can reopen it and carry it on. Identity is the anonymous token
// the loader minted in the host page's first-party storage and stamped into this
// iframe's URL - there is no login on a client's website to identify anyone by.
//
// Resume sends only a conversation *id*. The server rebuilds the context from
// the copy it recorded itself, so a transcript that has been through a browser
// can never be handed back as a system prompt.

const HISTORY_VISITOR_KEY = 'bx-avatar-panel-visitor';
const HISTORY_FLUSH_MS = 5000;
const VISITOR_ID_RE = /^[A-Za-z0-9_-]{16,128}$/;

const mintVisitorId = () => {
  const bytes = new Uint8Array(16);
  try {
    window.crypto.getRandomValues(bytes);
  } catch {
    for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
};

/**
 * The loader passes the token down in the URL, which is the path that survives
 * third-party storage partitioning. A raw iframe embed has no loader, so it
 * falls back to this document's own storage - partitioned per host site, which
 * still gives that visitor a stable thread on that one site.
 */
const resolveVisitorId = (urlParams) => {
  const fromUrl = urlParams.get('visitor');
  if (fromUrl && VISITOR_ID_RE.test(fromUrl)) return fromUrl;
  try {
    const stored = window.localStorage.getItem(HISTORY_VISITOR_KEY);
    if (stored && VISITOR_ID_RE.test(stored)) return stored;
    const minted = mintVisitorId();
    window.localStorage.setItem(HISTORY_VISITOR_KEY, minted);
    return minted;
  } catch {
    return null; // storage blocked: no stable identity, so no history
  }
};

const resolveHostOrigin = (urlParams) => {
  const fromUrl = urlParams.get('hostOrigin');
  if (fromUrl) return fromUrl;
  try {
    if (document.referrer) return new URL(document.referrer).origin;
  } catch { /* opaque referrer */ }
  return typeof window === 'undefined' ? '' : window.location.origin;
};

const relativeTime = (iso) => {
  if (!iso) return '';
  // Timestamps come back from the API as naive UTC, matching the rest of the
  // platform. Without the marker a browser reads them as local time and every
  // entry looks hours old, or from the future.
  const stamp = /(Z|[+-]\d{2}:?\d{2})$/.test(iso) ? iso : `${iso}Z`;
  const then = new Date(stamp).getTime();
  if (!Number.isFinite(then)) return '';
  const minutes = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
};

// ─── Style sheet ──────────────────────────────────────────────────────────────
// Keyframes, scrollbars and placeholder colour cannot be expressed inline, and
// the panel must not depend on the admin app's Tailwind build - it is served
// into a client's page.
const styleSheet = (t) => `
  .bx-ap * { box-sizing: border-box; }
  .bx-ap ::-webkit-scrollbar { width: 6px; height: 6px; }
  .bx-ap ::-webkit-scrollbar-track { background: transparent; }
  .bx-ap ::-webkit-scrollbar-thumb { background: ${rgba(t.text, 0.18)}; border-radius: 99px; }
  .bx-ap ::-webkit-scrollbar-thumb:hover { background: ${rgba(t.text, 0.3)}; }
  .bx-ap input::placeholder, .bx-ap textarea::placeholder { color: ${t.faint}; }
  .bx-ap button { font: inherit; cursor: pointer; }
  .bx-ap button:disabled { cursor: not-allowed; }
  .bx-ap-btn { transition: background-color .18s ease, color .18s ease, transform .18s ease, box-shadow .18s ease, opacity .18s ease; }
  .bx-ap-btn:hover:not(:disabled) { transform: translateY(-1px); }
  .bx-ap-btn:active:not(:disabled) { transform: translateY(0); }
  .bx-ap-ghost:hover:not(:disabled) { background: ${rgba(t.text, 0.08)} !important; }
  /* Controls that sit on top of the video, where a themed surface would vanish
     into whatever the avatar happens to be wearing. */
  .bx-ap-glass:hover:not(:disabled) { background: rgba(8, 12, 20, .82) !important; }
  .bx-ap-glass-danger:hover:not(:disabled) { background: rgba(226, 76, 76, .92) !important; }
  /* The floating card's own controls fade back when the visitor is not pointing
     at it, so a parked PIP window is just the avatar. The pop-out button in the
     panel does NOT use this - it is always visible. Always on for touch, which
     has no hover to give. */
  .bx-ap-pip-bar { opacity: .32; transition: opacity .18s ease; }
  .bx-ap-stage:hover .bx-ap-pip-bar, .bx-ap-stage:focus-within .bx-ap-pip-bar { opacity: 1; }
  @media (hover: none) { .bx-ap-pip-bar { opacity: 1; } }

  @keyframes bx-ap-in { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
  @keyframes bx-ap-pop { from { opacity: 0; transform: translateY(8px) scale(.97); } to { opacity: 1; transform: none; } }
  @keyframes bx-ap-pulse { 0% { box-shadow: 0 0 0 0 ${rgba(t.launcher, 0.5)}; } 70% { box-shadow: 0 0 0 ${SHADOW_REACH - 4}px ${rgba(t.launcher, 0)}; } 100% { box-shadow: 0 0 0 0 ${rgba(t.launcher, 0)}; } }
  @keyframes bx-ap-blink { 0%, 100% { opacity: 1; } 50% { opacity: .35; } }
  @keyframes bx-ap-dot { 0%, 80%, 100% { transform: translateY(0); opacity: .4; } 40% { transform: translateY(-4px); opacity: 1; } }
  @keyframes bx-ap-spin { to { transform: rotate(360deg); } }
  @keyframes bx-ap-sheen { 0% { transform: translateX(-120%); } 100% { transform: translateX(220%); } }
  @keyframes bx-ap-halo { 0% { transform: scale(.68); opacity: 0; } 18% { opacity: .5; } 100% { transform: scale(1.75); opacity: 0; } }
  @keyframes bx-ap-drift { 0%, 100% { transform: translate3d(-5%, -3%, 0) scale(1); } 50% { transform: translate3d(5%, 4%, 0) scale(1.14); } }
  @keyframes bx-ap-bob { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-3px); } }
  @keyframes bx-ap-track { 0% { transform: translateX(-110%); } 100% { transform: translateX(300%); } }

  .bx-ap-enter { animation: bx-ap-in .28s cubic-bezier(.22,.61,.36,1) both; }
  .bx-ap-pop { animation: bx-ap-pop .3s cubic-bezier(.22,.61,.36,1) both; }
  .bx-ap-spin { animation: bx-ap-spin .9s linear infinite; }
  .bx-ap-halo { animation: bx-ap-halo 2.6s cubic-bezier(.25,.6,.3,1) infinite; }
  .bx-ap-drift { animation: bx-ap-drift 11s ease-in-out infinite; }
  .bx-ap-bob { animation: bx-ap-bob 3.4s ease-in-out infinite; }

  @media (prefers-reduced-motion: reduce) {
    .bx-ap *, .bx-ap *::before, .bx-ap *::after {
      animation-duration: .01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: .01ms !important;
    }
    /* The travelling beam would park itself off-stage with no animation to
       carry it, leaving an empty rail. Fill the rail instead. */
    .bx-ap-beam { position: static !important; width: 100% !important; transform: none !important; }
  }
`;

// ─── Small pieces ─────────────────────────────────────────────────────────────

const BrandMark = ({ logoUrl, size, theme, icon }) => (
  <div
    style={{
      width: size,
      height: size,
      borderRadius: size * 0.32,
      background: logoUrl ? 'transparent' : theme.accentSoft,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      overflow: 'hidden',
    }}
  >
    {logoUrl ? (
      <img src={logoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
    ) : (
      React.createElement(icon || Sparkles, { size: Math.round(size * 0.5), color: theme.accent })
    )}
  </div>
);

const LiveDot = ({ color, size = 7 }) => (
  <span
    style={{
      width: size,
      height: size,
      borderRadius: '50%',
      background: color,
      display: 'inline-block',
      flexShrink: 0,
      animation: 'bx-ap-blink 2.2s ease-in-out infinite',
    }}
  />
);

const TypingDots = ({ color }) => (
  <span style={{ display: 'inline-flex', gap: 3, alignItems: 'center', height: 12 }}>
    {[0, 1, 2].map((i) => (
      <span
        key={i}
        style={{
          width: 5,
          height: 5,
          borderRadius: '50%',
          background: color,
          animation: `bx-ap-dot 1.1s ${i * 0.14}s ease-in-out infinite`,
        }}
      />
    ))}
  </span>
);

/*
 * Connecting state for the stage.
 *
 * A bare spinner reads as "nothing is happening" for the several seconds an
 * avatar session takes to come up (token, room join, avatar worker, first
 * video frame). This says what is happening instead: a silhouette waiting to
 * be filled, haloes leaving it, and a line of copy that advances through the
 * real stages so the wait feels accounted for rather than stalled.
 *
 * Everything sits on its own dark scrim so the copy stays legible on a light
 * theme and on top of a client's poster image alike.
 */
const CONNECT_STEPS = [
  'Securing your session',
  'Waking the avatar',
  'Warming up audio',
  'Almost there',
];

// The same wait with the avatar hidden. Nothing is being woken, so saying so
// would be describing a step that is not happening.
const CONNECT_STEPS_NO_AVATAR = [
  'Securing your session',
  'Warming up audio',
  'Almost there',
];

const AvatarSilhouette = ({ size, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="8.6" r="3.7" fill={color} />
    <path
      d="M4.8 20.4c0-3.9 3.2-6.5 7.2-6.5s7.2 2.6 7.2 6.5"
      stroke={color}
      strokeWidth="2.1"
      strokeLinecap="round"
      fill="none"
    />
  </svg>
);

const ConnectingStage = ({ theme, compact = false, logoUrl }) => {
  const [step, setStep] = useState(0);

  useEffect(() => {
    // Hold on the last line rather than looping. A list that restarts reads as
    // if the connection restarted with it.
    const id = setInterval(
      () => setStep((s) => (s < CONNECT_STEPS.length - 1 ? s + 1 : s)),
      1900
    );
    return () => clearInterval(id);
  }, []);

  const disc = compact ? 40 : 76;
  const glyph = Math.round(disc * 0.52);

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'absolute',
        inset: 0,
        display: 'grid',
        placeItems: 'center',
        overflow: 'hidden',
        background: 'linear-gradient(180deg, rgba(5,9,17,.62) 0%, rgba(5,9,17,.78) 100%)',
      }}
    >
      {/* Slow accent wash so the stage is never a flat dead rectangle. */}
      <div
        className="bx-ap-drift"
        style={{
          position: 'absolute',
          inset: '-30%',
          background: `radial-gradient(38% 38% at 33% 30%, ${rgba(theme.accent, 0.4)} 0%, transparent 70%),
                       radial-gradient(42% 42% at 68% 70%, ${rgba(theme.launcher, 0.26)} 0%, transparent 72%)`,
          filter: 'blur(4px)',
          pointerEvents: 'none',
        }}
      />

      <div
        style={{
          position: 'relative',
          display: 'grid',
          placeItems: 'center',
          gap: compact ? 0 : 18,
          padding: 12,
        }}
      >
        <div style={{ position: 'relative', width: disc, height: disc, display: 'grid', placeItems: 'center' }}>
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="bx-ap-halo"
              style={{
                position: 'absolute',
                inset: 0,
                borderRadius: '50%',
                border: `1px solid ${rgba(theme.accent, 0.75)}`,
                animationDelay: `${i * 0.87}s`,
                pointerEvents: 'none',
              }}
            />
          ))}

          {/* The seat the avatar drops into: a glass disc with a sheen sweep. */}
          <div
            className="bx-ap-bob"
            style={{
              position: 'relative',
              width: disc,
              height: disc,
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              overflow: 'hidden',
              background: `linear-gradient(160deg, ${rgba(theme.accent, 0.28)} 0%, rgba(255,255,255,.06) 100%)`,
              border: `1px solid ${rgba('#ffffff', 0.18)}`,
              boxShadow: `0 10px 30px ${rgba(theme.accent, 0.28)}`,
            }}
          >
            {logoUrl ? (
              <img
                src={logoUrl}
                alt=""
                style={{ width: '62%', height: '62%', objectFit: 'contain', opacity: 0.9 }}
              />
            ) : (
              <AvatarSilhouette size={glyph} color={rgba('#ffffff', 0.82)} />
            )}
            <span
              style={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: 0,
                width: '38%',
                background: `linear-gradient(90deg, transparent, ${rgba('#ffffff', 0.34)}, transparent)`,
                animation: 'bx-ap-sheen 2.2s ease-in-out infinite',
              }}
            />
          </div>
        </div>

        {!compact && (
          <div style={{ textAlign: 'center' }}>
            {/* Keyed so each line fades in on its own instead of swapping. */}
            <div
              key={step}
              className="bx-ap-enter"
              style={{ fontSize: 12.5, fontWeight: 600, color: '#fff', letterSpacing: 0.1 }}
            >
              {CONNECT_STEPS[step]}
            </div>
            <div
              style={{
                position: 'relative',
                width: 128,
                height: 3,
                margin: '11px auto 0',
                borderRadius: 99,
                overflow: 'hidden',
                background: rgba('#ffffff', 0.16),
              }}
            >
              <span
                className="bx-ap-beam"
                style={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: 0,
                  width: '40%',
                  borderRadius: 99,
                  background: `linear-gradient(90deg, transparent, ${theme.accent}, transparent)`,
                  animation: 'bx-ap-track 1.6s ease-in-out infinite',
                }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Connecting, with no stage to put it on.
 *
 * `ConnectingStage` above is painted on a dark scrim over the video box, which
 * is what lets it use white copy regardless of the client's palette. With the
 * avatar hidden there is no video box and no scrim, so this is the same idea
 * rebuilt in the panel's own theme: it has to be legible on a cream background
 * and on midnight alike, which is why every colour here comes off `theme`.
 *
 * It fills the transcript's box, above the composer, so the panel does not jump
 * when the words start arriving - the loader is replaced, nothing moves.
 */
const ConnectingBody = ({ theme, logoUrl, icon }) => {
  const [step, setStep] = useState(0);

  useEffect(() => {
    // Holds on the last line rather than looping, for the same reason the stage
    // version does: a list that restarts reads as a connection that restarted.
    const id = setInterval(
      () => setStep((s) => (s < CONNECT_STEPS_NO_AVATAR.length - 1 ? s + 1 : s)),
      1900
    );
    return () => clearInterval(id);
  }, []);

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        flex: 1,
        minHeight: 0,
        display: 'grid',
        placeItems: 'center',
        padding: '18px 22px',
        textAlign: 'center',
      }}
    >
      <div style={{ display: 'grid', placeItems: 'center', gap: 17 }}>
        <div style={{ position: 'relative', width: 74, height: 74, display: 'grid', placeItems: 'center' }}>
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="bx-ap-halo"
              style={{
                position: 'absolute',
                inset: 0,
                borderRadius: '50%',
                border: `1px solid ${rgba(theme.accent, 0.55)}`,
                animationDelay: `${i * 0.87}s`,
                pointerEvents: 'none',
              }}
            />
          ))}

          <div
            className="bx-ap-bob"
            style={{
              position: 'relative',
              width: 74,
              height: 74,
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              overflow: 'hidden',
              background: `linear-gradient(160deg, ${rgba(theme.accent, 0.26)} 0%, ${rgba(theme.accent, 0.05)} 100%)`,
              border: `1px solid ${rgba(theme.accent, 0.3)}`,
              boxShadow: `0 10px 28px ${rgba(theme.accent, 0.2)}`,
            }}
          >
            {logoUrl ? (
              <img
                src={logoUrl}
                alt=""
                style={{ width: '58%', height: '58%', objectFit: 'contain' }}
              />
            ) : (
              React.createElement(icon || Sparkles, { size: 30, color: theme.accent })
            )}
            <span
              style={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: 0,
                width: '38%',
                background: `linear-gradient(90deg, transparent, ${rgba(theme.text, 0.14)}, transparent)`,
                animation: 'bx-ap-sheen 2.2s ease-in-out infinite',
              }}
            />
          </div>
        </div>

        <div>
          {/* Keyed so each line fades in on its own instead of swapping. */}
          <div
            key={step}
            className="bx-ap-enter"
            style={{ fontSize: 13.5, fontWeight: 650, color: theme.text, letterSpacing: '-0.01em' }}
          >
            {CONNECT_STEPS_NO_AVATAR[step]}
          </div>
          <div style={{ fontSize: 11.5, lineHeight: 1.5, color: theme.faint, marginTop: 5 }}>
            This takes a couple of seconds.
          </div>
          <div
            style={{
              position: 'relative',
              width: 126,
              height: 3,
              margin: '13px auto 0',
              borderRadius: 99,
              overflow: 'hidden',
              background: rgba(theme.text, 0.12),
            }}
          >
            <span
              className="bx-ap-beam"
              style={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: 0,
                width: '40%',
                borderRadius: 99,
                background: `linear-gradient(90deg, transparent, ${theme.accent}, transparent)`,
                animation: 'bx-ap-track 1.6s ease-in-out infinite',
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const IconButton = ({ onClick, label, theme, children, tone = 'ghost', size = 34 }) => {
  const tones = {
    ghost: { background: 'transparent', color: theme.muted },
    solid: { background: theme.accent, color: theme.accentText },
    danger: { background: theme.dangerSoft, color: theme.danger },
  };
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className={`bx-ap-btn ${tone === 'ghost' ? 'bx-ap-ghost' : ''}`}
      style={{
        width: size,
        height: size,
        borderRadius: 10,
        border: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        ...tones[tone],
      }}
    >
      {children}
    </button>
  );
};

/**
 * A control that sits on the video itself: glass rather than themed, because a
 * surface colour would disappear into whatever the avatar is wearing. Marked
 * `data-bx-nodrag` so pressing it moves nothing - the PIP card treats every
 * other pixel as a drag handle.
 */
const PipButton = ({ onClick, label, children, danger = false, size = 30 }) => (
  <button
    type="button"
    data-bx-nodrag=""
    onClick={onClick}
    onPointerDown={(e) => e.stopPropagation()}
    aria-label={label}
    title={label}
    className={`bx-ap-btn ${danger ? 'bx-ap-glass-danger' : 'bx-ap-glass'}`}
    style={{
      width: size,
      height: size,
      borderRadius: 9,
      border: 'none',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      background: danger ? 'rgba(206, 62, 62, .82)' : 'rgba(8, 12, 20, .58)',
      backdropFilter: 'blur(6px)',
      WebkitBackdropFilter: 'blur(6px)',
      color: '#fff',
      cursor: 'pointer',
    }}
  >
    {children}
  </button>
);

// ─── Main component ───────────────────────────────────────────────────────────

const AvatarPanelV2 = ({ agentId }) => {
  const urlParams = useMemo(
    () => new URLSearchParams(typeof window === 'undefined' ? '' : window.location.search),
    []
  );

  // `launcher` is the embeddable behaviour (launcher -> panel -> mini pill).
  // `panel` is a bare always-open panel for raw iframe embeds and previews.
  const usesLoader = urlParams.get('embed') === 'loader';
  const viewMode = useMemo(() => {
    const v = urlParams.get('view');
    if (v === 'launcher' || v === 'panel' || v === 'full') return v;
    return usesLoader ? 'launcher' : 'panel';
  }, [urlParams, usesLoader]);
  const alwaysOpen = viewMode !== 'launcher';
  const cobrowseRequested = urlParams.get('cobrowse') === '1';

  const [agent, setAgent] = useState(null);
  // Visitor-attached images (agent setting chat_image_upload_enabled).
  const attachments = useChatImageAttachments(agentId, undefined, agent?.chat_image_limits?.per_message);
  const imageInputRef = useRef(null);
  const imagesEnabled = Boolean(agent?.chat_image_upload_enabled) && modelSupportsImages(agent?.llm_model);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [mode, setMode] = useState(() => {
    if (alwaysOpen) return 'open';
    // A co-browse call the previous page just released reopens as it was left
    // (the loader read it from the call record), instead of flashing the
    // launcher first. PIP needs a live session, so it is restored on rejoin.
    const carried = cobrowseRequested ? urlParams.get('cbMode') : null;
    if (carried === 'mini') return 'mini';
    if (carried === 'open' || carried === 'pip') return 'open';
    return 'closed';
  }); // closed | open | mini | pip
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [captions, setCaptions] = useState([]);
  const [room, setRoom] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [avatarVideoTrack, setAvatarVideoTrack] = useState(null);
  const [micEnabled, setMicEnabled] = useState(true);
  const [textValue, setTextValue] = useState('');
  const [sending, setSending] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [agentThinking, setAgentThinking] = useState(false);
  const [teaserVisible, setTeaserVisible] = useState(false);
  // Transparent gutter the loader kept on each side of the iframe for shadows.
  // Seeded with the loader's nominal gutter so the first painted frame already
  // sits in the right place; the loader's own report corrects any side it had
  // to squeeze against the viewport. Zero without a loader: a fixed-position
  // widget on a real page has no clipping edge, so it needs no gutter.
  // A callback ref, not a useRef: the launcher does not exist on first render
  // (the agent is still loading), and a ref gives an effect no way to hear about
  // the node arriving later. Holding the node in state re-runs the measuring
  // effect the moment it mounts. With a plain ref the effect bound to `null`,
  // never re-ran, and the loader was left sizing the iframe from its
  // placeholder guess - a clipped launcher shoved past the screen corner.
  const [launcherNode, setLauncherNode] = useState(null);
  // PIP: `pipDragging` only drives the cursor and the lifted shadow, so it
  // changes twice per drag rather than per pointer move. `pipPos` is the
  // standalone fallback - under the loader the position lives there, because the
  // thing being moved is the iframe and only the loader can move it.
  const [pipDragging, setPipDragging] = useState(false);
  const [pipPos, setPipPos] = useState(null);
  const pipDragRef = useRef(null);
  const [insets, setInsets] = useState(() =>
    usesLoader ? { l: SHADOW_REACH, t: SHADOW_REACH, r: SHADOW_REACH, b: SHADOW_REACH } : NO_INSET
  );
  // The host page's viewport, which is NOT this iframe's viewport. Under the
  // loader the iframe is only as big as the widget, so anything that should
  // respond to "how much room does the visitor have" has to read this instead.
  // Seeded from the URL the loader built, then kept current over postMessage.
  const [hostViewport, setHostViewport] = useState(() => {
    const fromUrl = (key) => parseInt(urlParams.get(key), 10);
    const w = fromUrl('hostW');
    const h = fromUrl('hostH');
    if (Number.isFinite(w) && Number.isFinite(h)) return { vw: w, vh: h };
    // No loader: this document's own viewport is the real one.
    return typeof window === 'undefined'
      ? { vw: 1280, vh: 800 }
      : { vw: window.innerWidth, vh: window.innerHeight };
  });
  const [hostSize, setHostSize] = useState(() => ({
    w: typeof window === 'undefined' ? 420 : window.innerWidth,
    h: typeof window === 'undefined' ? 640 : window.innerHeight,
  }));
  const [dynamicVariableNames, setDynamicVariableNames] = useState([]);
  const [dynamicVariableValues, setDynamicVariableValues] = useState({});

  // ── History ────────────────────────────────────────────────────────────────
  // `pane` swaps what the panel body shows without unmounting anything else -
  // the stage is a sibling, so the avatar keeps playing while the visitor reads
  // back an old conversation.
  const [pane, setPane] = useState('live'); // live | history | historyDetail
  const [historyList, setHistoryList] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyDetail, setHistoryDetail] = useState(null);
  const [historyBusy, setHistoryBusy] = useState(false);
  const visitorId = useMemo(() => resolveVisitorId(urlParams), [urlParams]);
  const hostOrigin = useMemo(() => resolveHostOrigin(urlParams), [urlParams]);
  // The row this session is being written to, and the conversation it continues.
  // Refs rather than state: they are read inside callbacks and during page
  // unload, where a stale render closure would send the wrong id or none at all.
  const conversationIdRef = useRef(null);
  const resumeFromRef = useRef(null);
  const sessionIdRef = useRef(null);
  const captionsRef = useRef([]);
  const flushDirtyRef = useRef(false);
  const flushingRef = useRef(false);

  const hostRef = useRef(null);
  const audioRef = useRef(null);
  const videoRef = useRef(null);
  const stageRingRef = useRef(null);
  const micRingRef = useRef(null);
  const videoAttachedRef = useRef(false);
  const avatarReadyNotifiedRef = useRef(false);
  const transcriptEndRef = useRef(null);
  const captionSeqRef = useRef(0);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const analyserDataRef = useRef(null);
  // The meter is on the avatar's own audio (see the TrackSubscribed handler)
  const analyserFromAvatarRef = useRef(false);
  const micAnalyserRef = useRef(null);
  const micDataRef = useRef(null);
  const revealTimerRef = useRef(null);
  const startedRef = useRef(false);
  // When the call started, so the clock survives a co-browse page load
  const clockStartRef = useRef(null);
  const revealRef = useRef({
    segments: [],
    charsPerSec: DEFAULT_CHARS_PER_SEC,
    lastVoiceAt: 0,
    lastTickAt: 0,
    peakLevel: 0,      // decaying loudest RMS, for the quiet-track voice gate
    carryVoice: false, // hand the voice clock to the next segment, mid-breath
    bargeInAt: 0,      // when the visitor last started a turn of their own
    cutTurns: [],      // full text of turns the visitor cut off
  });

  const settings = useMemo(() => getAvatarPanelSettings(agent, urlParams), [agent, urlParams]);
  const imageDrop = useImageDrop(imagesEnabled && settings.showTextInput, attachments.addFiles);
  const theme = useMemo(() => buildAvatarPanelTheme(settings), [settings]);
  const hasSession = isConnected || isConnecting;
  // Read everywhere instead of `settings.hideAvatar` so a per-page URL override
  // is honoured on exactly the same terms as the saved config.
  const avatarHidden = !!settings.hideAvatar;
  const pipSize = useMemo(
    () => avatarPipSize(settings, hostViewport.vw, hostViewport.vh),
    [settings, hostViewport.vw, hostViewport.vh]
  );
  // Same deal as `pipSize`: arithmetic on the settings and the host viewport, so
  // it needs no measurement round trip and can travel to the loader with the
  // mode that uses it.
  const spotlightBox = useMemo(
    () => avatarSpotlightSize(settings, hostViewport.vw, hostViewport.vh),
    [settings, hostViewport.vw, hostViewport.vh]
  );
  // Popping out only makes sense for a widget that owns its own rectangle on the
  // page. An inline `view=panel` iframe is sized by the host document's layout,
  // so a card that floated out of it would just be clipped by the frame.
  // There is also nothing to pop out when the avatar is hidden.
  const pipAvailable = !alwaysOpen && settings.pipEnabled && !avatarHidden;

  // ── Dynamic variables from the query string (Name, Email, ...) ─────────────
  useEffect(() => {
    const systemParams = new Set([
      'view', 'embed', 'agent', 'token', 'embed_token', 'key', 'hostW', 'hostH',
      // Plumbing from the loader, not something the visitor is being asked for.
      // Missing from this list they would be collected on the start screen as
      // fields to fill in and then sent to the agent as dynamic variables.
      'visitor', 'hostOrigin', 'cobrowse', 'cbMode',
      ...STR_PARAMS, ...NUM_PARAMS, ...BOOL_PARAMS, ...Object.keys(HEX_PARAMS),
    ]);
    const names = [];
    const initial = {};
    urlParams.forEach((value, key) => {
      if (systemParams.has(key)) return;
      names.push(key);
      if (value && !value.startsWith('{{') && !value.endsWith('}}')) initial[key] = value;
    });
    setDynamicVariableNames(names);
    setDynamicVariableValues(initial);
  }, [urlParams]);

  // ── Agent config ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!agentId) { setLoading(false); return; }
    let cancelled = false;
    api.get(`/agents/${agentId}`)
      .then((response) => {
        if (cancelled) return;
        if (typeof response.data === 'string' && response.data.trim().startsWith('<!DOCTYPE')) {
          setError('Unexpected response from the API. Check the embed host configuration.');
          return;
        }
        setAgent(response.data);
        const raw = response.data.liveavatar_enabled;
        const enabled = raw === true || raw === 'true' || raw === 1 || raw === '1';
        const avatarId = response.data.liveavatar_avatar_id;
        // With the avatar hidden nothing ever asks LiveAvatar for a face, so a
        // missing avatar id is no reason to refuse the conversation. The panel
        // still rides the LiveAvatar embed path for its streaming transcript,
        // so `liveavatar_enabled` is still required either way.
        const hidden = !!getAvatarPanelSettings(response.data, urlParams).hideAvatar;
        if (!enabled) setError('LiveAvatar is not enabled for this agent.');
        else if (!hidden && (!avatarId || !String(avatarId).trim())) setError('Avatar ID is missing for this agent.');
      })
      .catch((err) => {
        if (!cancelled) setError(`Agent not found: ${err.response?.data?.detail || err.message}`);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [agentId, urlParams]);

  // ── Transparent page behind the widget so the host page shows through ──────
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById('root');

    // This document is a widget, not a page: it must never grow its own
    // scrollbars. Every scrolling region the panel needs (the transcript, the
    // idle screen) declares its own overflow.
    //
    // Without this the widget deadlocks at a screen corner. The loader sizes
    // the iframe to the widget plus a gutter, and near an edge that gutter is
    // clamped. A vertical scrollbar then eats ~15px of client width, leaving
    // about a pixel of slack against the inner offset. One sub-pixel over and a
    // horizontal scrollbar appears, which eats ~15px of height, which forces
    // the vertical one to stay. Neither can retract, and the launcher is left
    // clipped by exactly the scrollbar thickness.
    const fill = {
      margin: '0',
      padding: '0',
      width: '100%',
      height: '100%',
      overflow: 'hidden',
    };
    const transparent = viewMode === 'launcher' ? { background: 'transparent' } : {};

    // Applied property by property, not through cssText: assigning cssText
    // re-serializes the whole declaration block and drops anything the engine
    // does not round-trip.
    const applied = [];
    const apply = (el, styles) => {
      if (!el) return;
      Object.keys(styles).forEach((key) => {
        applied.push([el, key, el.style[key]]);
        el.style[key] = styles[key];
      });
    };

    apply(html, { ...fill, ...transparent });
    apply(body, { ...fill, ...transparent });
    apply(root, { height: '100%', ...transparent });

    return () => {
      // Restore in reverse, so a property touched twice ends on its original value.
      for (let i = applied.length - 1; i >= 0; i -= 1) {
        const [el, key, value] = applied[i];
        el.style[key] = value;
      }
    };
  }, [viewMode]);

  // Without a loader there is no `MSG_VIEWPORT` coming, so this document has to
  // watch its own window - otherwise a spotlight sized from the viewport would
  // be sized from whatever the viewport was at mount and never move again.
  // Guarded, because under the loader `window.innerWidth` is the iframe's width,
  // which in launcher mode is roughly the width of a button.
  useEffect(() => {
    if (usesLoader || typeof window === 'undefined') return undefined;
    const onResize = () => setHostViewport((prev) => (
      prev.vw === window.innerWidth && prev.vh === window.innerHeight
        ? prev
        : { vw: window.innerWidth, vh: window.innerHeight }
    ));
    window.addEventListener('resize', onResize);
    onResize();
    return () => window.removeEventListener('resize', onResize);
  }, [usesLoader]);

  // ── Measure the host box; every geometry number is derived from it ─────────
  // The box only exists once the agent has loaded (a spinner renders first), so
  // `loading` and `agent` are dependencies too. Without them a panel that opens
  // without a mode change - a co-browse call carried onto a new page starts
  // already open - never measures, and the stage keeps the size of the loader's
  // placeholder frame.
  useEffect(() => {
    const el = hostRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => {
      setHostSize({ w: el.clientWidth, h: el.clientHeight });
    });
    observer.observe(el);
    setHostSize({ w: el.clientWidth, h: el.clientHeight });
    return () => observer.disconnect();
  }, [mode, loading, agent]);

  // ── Caption plumbing ───────────────────────────────────────────────────────

  const upsertCaption = useCallback((key, speaker, text, opts = {}) => {
    const clean = normalizeText(text);
    if (!clean) return;
    const cut = !!opts.cut;
    const now = Date.now();
    // The visitor's words also arrive as other copies (transcriptFold.js)
    const fold = (list, at) => (speaker === 'user' ? foldCallerTurn(list, at, { start: liveStart(list) }) : list);
    setCaptions((prev) => {
      const index = prev.findIndex((c) => c.key === key);
      if (index !== -1) {
        // A bubble holding a cut-off turn holds exactly the words the visitor
        // heard. Nothing may grow it back into the reply the LLM wrote.
        if (prev[index].cut && !cut) return prev;
        if (prev[index].text === clean && !!prev[index].cut === cut) return prev;
        const next = [...prev];
        next[index] = { ...next[index], text: clean, cut: cut || !!next[index].cut, updatedAt: now };
        return fold(next, index);
      }
      // This turn may already have a bubble under a different key: the backend
      // publishes every turn on the data channel, and LiveKit republishes one
      // utterance under a fresh segment id whenever its transcription output is
      // not mid-capture - a second final event for the same words then arrives on
      // a key never seen before. Take the bubble over instead of adding a second
      // one for the same words. Never reach past the resume boundary - see
      // liveStart.
      const from = Math.max(liveStart(prev), prev.length - 3);
      const adoptIndex = prev.findIndex(
        (c, i) =>
          i >= from &&
          isSameTurn(c, speaker, clean) &&
          // A bubble from the data channel is this turn wherever it sits. One
          // that streamed has to still be open, and to be growing rather than
          // shrinking, or two separate turns could be folded together.
          (!isStreamedKey(c.key) || (stillOpen(c, now) && clean.length >= c.text.length))
      );
      if (adoptIndex !== -1) {
        const next = [...prev];
        const held = next[adoptIndex];
        next[adoptIndex] = {
          ...held,
          key,
          text: (cut || held.cut) ? shorterOf(held.text, clean) : longerOf(held.text, clean),
          cut: cut || !!held.cut,
          updatedAt: now,
        };
        return fold(next, adoptIndex);
      }
      return fold([...prev, { key, speaker, text: clean, at: now, updatedAt: now, cut }], prev.length);
    });
  }, []);

  const addCaption = useCallback((speaker, text, images) => {
    const clean = normalizeText(text);
    if (!clean) return;
    const state = revealRef.current;
    const now = Date.now();
    if (speaker !== 'user') {
      // An agent turn still being released stays under the reveal loop's
      // control, so the committed copy must not race ahead and dump the rest on
      // screen.
      if (state.segments.some((s) => isSameTurn({ speaker, text: s.full }, speaker, clean))) {
        return;
      }
      // A turn already retired as cut keeps the words that were spoken. The
      // backend republishes every turn once it is committed to history, and for
      // a cut turn that copy still carries the whole reply.
      if (state.cutTurns.some((full) => isSameTurn({ speaker: 'ai', text: full }, 'ai', clean))) {
        return;
      }
    }
    captionSeqRef.current += 1;
    const key = `turn-${captionSeqRef.current}`;
    setCaptions((prev) => {
      // Both windows are clamped to the current session - see liveStart.
      const start = liveStart(prev);
      const shown = compareKey(clean);
      if (prev.slice(Math.max(start, prev.length - 8))
        .some((c) => c.speaker === speaker && compareKey(c.text) === shown)) return prev;
      // A live bubble for this turn that has not caught up yet. The visitor's
      // spoken turn used to be looked for in the newest bubble alone, on the
      // grounds that it is the newest while it streams. It is not: the agent's
      // own paced bubble is appended while the visitor is still talking, and the
      // committed copy then landed as a second bubble for words already on
      // screen.
      const from = Math.max(start, prev.length - 3);
      const mergeIndex = prev.findIndex(
        (c, i) =>
          i >= from &&
          isSameTurn(c, speaker, clean) &&
          (speaker !== 'user' || (isStreamedKey(c.key) && stillOpen(c, now)))
      );
      if (mergeIndex !== -1) {
        const next = [...prev];
        const held = next[mergeIndex];
        next[mergeIndex] = {
          ...held,
          // A cut turn can only shrink: this copy came from the LLM, and the
          // words past the cut were never spoken.
          text: held.cut ? shorterOf(held.text, clean) : longerOf(held.text, clean),
          updatedAt: now,
          ...(images?.length && !held.images ? { images } : {}),
        };
        return speaker === 'user' && !images?.length ? foldCallerTurn(next, mergeIndex, { start }) : next;
      }
      const added = [...prev, { key, speaker, text: clean, at: now, updatedAt: now, ...(images?.length ? { images } : {}) }];
      // The committed copy of a turn already on screen in parts (transcriptFold.js)
      return speaker === 'user' && !images?.length ? foldCallerTurn(added, prev.length, { start }) : added;
    });
  }, []);

  /**
   * The visitor has started a turn of their own, which is what stops the avatar
   * mid-sentence.
   *
   * Only the first stamp taken against the turn now playing counts. LiveKit
   * publishes the visitor's interim and final transcripts as separate streams,
   * and letting the final one move the stamp would push a real barge-in past the
   * moment the avatar fell silent, where it reads as a reply that came after.
   */
  const markBargeIn = useCallback(() => {
    const state = revealRef.current;
    const head = state.segments[0];
    if (!head || !head.activatedAt || state.bargeInAt < head.activatedAt) {
      state.bargeInAt = Date.now();
    }
  }, []);

  /** Attach an analyser to a track without routing it to the speakers. */
  const makeAnalyser = useCallback((track) => {
    try {
      const mediaStreamTrack = track?.mediaStreamTrack;
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!mediaStreamTrack || !AudioCtx) return null;
      if (!audioCtxRef.current) audioCtxRef.current = new AudioCtx();
      const ctx = audioCtxRef.current;
      ctx.resume?.().catch(() => {});
      const source = ctx.createMediaStreamSource(new MediaStream([mediaStreamTrack]));
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);
      return { analyser, data: new Uint8Array(analyser.fftSize) };
    } catch {
      return null;
    }
  }, []);

  const readLevel = (analyser, data) => {
    if (!analyser || !data) return null;
    analyser.getByteTimeDomainData(data);
    let sum = 0;
    for (let i = 0; i < data.length; i += 1) {
      const sample = (data[i] - 128) / 128;
      sum += sample * sample;
    }
    return Math.sqrt(sum / data.length);
  };

  const pushAgentText = useCallback((key, text, closed) => {
    const clean = normalizeText(text);
    if (!clean) return;
    const state = revealRef.current;
    const now = Date.now();
    let segment = state.segments.find((s) => s.key === key);
    if (!segment) {
      segment = {
        key,
        full: clean,
        shown: 0,
        closed: false,
        spokenMs: 0,
        blindMs: 0,  // time revealed with no measurable audio to pace against
        activatedAt: 0,
        grewAt: now, // last time more text arrived, for the orphan watchdog
      };
      state.segments.push(segment);
    }
    if (clean.length >= segment.full.length) {
      if (clean.length > segment.full.length) segment.grewAt = now;
      segment.full = clean;
    }
    if (closed) segment.closed = true;
  }, []);

  // The reveal loop doubles as the meter loop. Levels are written straight to
  // the DOM rather than to state: at 12.5 fps a re-render of the whole panel
  // (transcript included) for a ring animation is not worth paying for.
  const tickReveal = useCallback(() => {
    const state = revealRef.current;
    const now = Date.now();
    const dtMs = state.lastTickAt ? Math.min(now - state.lastTickAt, MAX_TICK_MS) : REVEAL_TICK_MS;
    state.lastTickAt = now;

    // A suspended audio context (a co-browse page load leaves the new page
    // without a user gesture) reads as silence, which would hold every reply
    // back for NO_AUDIO_FALLBACK_MS. It is no measurement at all, so treat it
    // like no meter and pace the text on its own from the first word.
    const ctxState = audioCtxRef.current?.state;
    const level = ctxState && ctxState !== 'running' ? null : readLevel(analyserRef.current, analyserDataRef.current);
    if (stageRingRef.current) {
      const scaled = Math.min(1, (level || 0) * 9);
      stageRingRef.current.style.setProperty('--bx-level', scaled.toFixed(3));
    }
    if (micRingRef.current) {
      const micLevel = readLevel(micAnalyserRef.current, micDataRef.current);
      micRingRef.current.style.setProperty('--bx-level', Math.min(1, (micLevel || 0) * 9).toFixed(3));
    }

    // Voice gate, relative to the loudest thing recently heard on this track so
    // a quiet mix still reads as speech - see QUIET_TRACK_FLOOR.
    if (level !== null) state.peakLevel = Math.max(level, state.peakLevel * PEAK_DECAY_PER_TICK);
    const gate =
      state.peakLevel >= VOICE_RMS_THRESHOLD || state.peakLevel < QUIET_TRACK_FLOOR
        ? VOICE_RMS_THRESHOLD
        : state.peakLevel * QUIET_GATE_RATIO;
    const speaking = level === null ? null : level > gate;
    if (speaking) state.lastVoiceAt = now;

    const segment = state.segments[0];
    if (!segment) return;

    if (!segment.activatedAt) {
      segment.activatedAt = now;
      // Segments that run back to back in one breath keep the voice clock, so
      // the next one carries on against audio that is already playing instead
      // of waiting out a silence that is never coming.
      if (state.carryVoice) state.carryVoice = false;
      else state.lastVoiceAt = speaking ? now : 0;
    }

    const heardVoice = state.lastVoiceAt > 0;

    // A reply the avatar never started to say. It arrived, the visitor carried
    // on talking (they had only paused), and not a sound of it was heard: the
    // agent cancelled it and will answer the whole turn instead. It is held
    // rather than written blind, and dropped once the next reply is queued
    // behind it (or after UNSPOKEN_REPLY_MS). Only with a working meter, which
    // is what makes "nothing was heard" a fact.
    const unspoken = speaking !== null && !heardVoice && segment.shown === 0 &&
      state.bargeInAt >= segment.activatedAt;
    if (unspoken) {
      if (state.segments.length > 1 || now - state.bargeInAt > UNSPOKEN_REPLY_MS) {
        state.cutTurns.push(segment.full); // its committed copy must not land either
        if (state.cutTurns.length > CUT_MEMORY) state.cutTurns.shift();
        state.segments.shift();
        setAgentThinking(false);
      }
      return;
    }

    const noAudioSignal =
      speaking === null || (!heardVoice && now - segment.activatedAt > NO_AUDIO_FALLBACK_MS);
    const withinSpeech = speaking || (heardVoice && now - state.lastVoiceAt < VOICE_HANGOVER_MS);

    if (withinSpeech || noAudioSignal) {
      segment.shown = Math.min(segment.full.length, segment.shown + (state.charsPerSec * dtMs) / 1000);
      segment.spokenMs += dtMs;
      if (!withinSpeech) segment.blindMs += dtMs;
      upsertCaption(segment.key, 'ai', revealedText(segment.full, segment.shown));
    }

    const retire = (text, opts) => {
      if (text) upsertCaption(segment.key, 'ai', text, opts);
      state.segments.shift();
      setAgentThinking(false);
    };

    const fullyRevealed = segment.shown >= segment.full.length;
    const silenceMs = heardVoice ? now - state.lastVoiceAt : 0;
    const audioEnded = heardVoice ? silenceMs > END_OF_SPEECH_SILENCE_MS : fullyRevealed;

    // A long answer arrives as several segments back to back. Holding the head of
    // the queue until the room goes quiet leaves the rest waiting behind audio
    // that is already playing, which is how the transcript ends up a turn behind
    // and then dumps the backlog in one go. Everything that arrived is on screen,
    // so hand the voice clock over and let the next segment carry on.
    if (segment.closed && fullyRevealed && !audioEnded && state.segments.length > 1) {
      state.carryVoice = true;
      retire(segment.full);
      return;
    }

    if (!audioEnded) return;
    // The stream never closed. Give it a moment in case more text is still on
    // its way, then let the segment go rather than jam every turn behind it.
    if (!segment.closed && now - segment.grewAt < ORPHAN_SEGMENT_MS) return;

    // A barge-in cuts the audio within a moment of the visitor starting to
    // speak, so a visitor turn that opened around the same time as the avatar
    // fell silent is what stopped it. A backchannel earlier in the answer, or a
    // question asked well after the agent finished, is not.
    const bargedIn =
      state.bargeInAt >= segment.activatedAt &&
      state.bargeInAt >= state.lastVoiceAt - BARGE_IN_LEAD_MS &&
      state.bargeInAt <= state.lastVoiceAt + BARGE_IN_LAG_MS;

    if (!fullyRevealed && !bargedIn &&
        silenceMs < END_OF_SPEECH_SILENCE_MS + BARGE_IN_LAG_MS) {
      // The agent stopped with text still held back, and a barge-in is only
      // known here once the visitor's own words reach this document, which
      // trails the moment the avatar went quiet. Wait that out before writing a
      // tail that may never have been spoken.
      return;
    }

    if (!fullyRevealed && bargedIn) {
      // Cut off part way through: the rest of the reply was never spoken, so it
      // is never written. Remember the whole turn, because the backend publishes
      // its committed copy afterwards and that copy still carries the words the
      // avatar never got to. Nothing is learned from a cut turn - see
      // learnSpeakingRate.
      state.cutTurns.push(segment.full);
      if (state.cutTurns.length > CUT_MEMORY) state.cutTurns.shift();
      const spoken = revealedText(segment.full, segment.shown);
      retire(spoken ? withCutMarker(spoken) : '', { cut: true });
      return;
    }

    learnSpeakingRate(state, segment);
    retire(segment.full);
  }, [upsertCaption]);

  // A browser starts the audio context suspended when this document has had no
  // tap or key yet (a co-browse call carried onto a new page). The first one
  // brings the meter back; until then the reveal paces itself (tickReveal).
  useEffect(() => {
    const resume = () => {
      const ctx = audioCtxRef.current;
      if (ctx && ctx.state === 'suspended') ctx.resume().catch(() => {});
    };
    window.addEventListener('pointerdown', resume, true);
    window.addEventListener('keydown', resume, true);
    return () => {
      window.removeEventListener('pointerdown', resume, true);
      window.removeEventListener('keydown', resume, true);
    };
  }, []);

  const stopReveal = useCallback(() => {
    if (revealTimerRef.current) {
      clearInterval(revealTimerRef.current);
      revealTimerRef.current = null;
    }
    resetRevealState(revealRef.current);
    analyserRef.current = null;
    analyserDataRef.current = null;
    analyserFromAvatarRef.current = false;
    micAnalyserRef.current = null;
    micDataRef.current = null;
  }, []);

  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [captions, mode]);

  // Session clock
  useEffect(() => {
    if (!isConnected) { setElapsed(0); return undefined; }
    const started = clockStartRef.current || Date.now();
    const tick = () => setElapsed(Math.max(0, Math.floor((Date.now() - started) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [isConnected]);

  // ── Co-browse ──────────────────────────────────────────────────────────────

  // Resolves once the avatar has finished speaking. The worker cannot tell -
  // the avatar never reports playback finished - but this document hears the
  // avatar's audio and paces the transcript against it, so a page change waits
  // here for "one moment, I'll take you there" to be said in full.
  const waitForSpeechEnd = useCallback(() => new Promise((resolve) => {
    const startedAt = Date.now();
    const check = () => {
      const state = revealRef.current;
      const now = Date.now();
      const quietFor = state.lastVoiceAt ? now - state.lastVoiceAt : Infinity;
      if (
        now - startedAt >= COBROWSE_SPEECH_START_GRACE_MS &&
        state.segments.length === 0 &&
        quietFor >= COBROWSE_SPEECH_QUIET_MS
      ) {
        resolve();
        return;
      }
      setTimeout(check, 100);
    };
    check();
  }), []);

  // Declared below; the bridge only calls it from an event, never on render.
  const resumeCobrowseRef = useRef(null);
  const cobrowse = useCobrowseBridge({
    api,
    agentId,
    requested: cobrowseRequested,
    claimedOrigin: hostOrigin,
    onResume: (record) => resumeCobrowseRef.current?.(record),
    waitForSpeechEnd,
  });
  // Callbacks read the bridge through a ref so they need not be rebuilt every
  // time one of its flags changes.
  const cobrowseRef = useRef(cobrowse);
  cobrowseRef.current = cobrowse;
  // The page-control notice goes on the side of the page the panel is not on
  const cobrowsePillSide = (
    settings.variant === 'spotlight'
      ? String(settings.position || '').includes('left')
      : settings.side === 'left'
  ) ? 'right' : 'left';

  const attachVideoTrack = useCallback((track) => {
    if (!track || !videoRef.current || videoAttachedRef.current) return videoAttachedRef.current;
    try {
      track.attach(videoRef.current);
      if (!videoRef.current.srcObject && track.mediaStreamTrack) {
        try {
          videoRef.current.srcObject = new MediaStream([track.mediaStreamTrack]);
        } catch { /* attach already handled it */ }
      }
      videoAttachedRef.current = true;
      setTimeout(() => {
        if (videoRef.current?.srcObject) videoRef.current.play().catch(() => {});
      }, 300);
      return true;
    } catch {
      return false;
    }
  }, []);

  // ── History ────────────────────────────────────────────────────────────────

  // Three things have to line up for history to exist at all: the client turned
  // it on, the visitor has a stable anonymous id, and there is an agent to scope
  // it to. Any one missing and the feature disappears entirely rather than
  // half-working - no clock in the header, and nothing written anywhere.
  const historyAvailable = !!(settings.historyEnabled && visitorId && agent);

  // The panel always holds the full transcript it is showing, so it sends the
  // whole thing rather than a delta. That makes the write idempotent, which is
  // what lets the last flush of a session go out during page unload without
  // caring whether it lands late, twice, or out of order.
  const flushHistory = useCallback(async ({ unloading = false } = {}) => {
    if (!historyAvailable) return null;
    const turns = captionsRef.current
      .filter((c) => c.text && c.text.trim())
      .map((c) => ({ speaker: c.speaker === 'user' ? 'user' : 'ai', text: c.text, at: c.at }));
    if (turns.length === 0) return null;

    const payload = {
      agent_id: agent.id,
      visitor_id: visitorId,
      turns,
      session_id: sessionIdRef.current || undefined,
      conversation_id: conversationIdRef.current || undefined,
      resumed_from: resumeFromRef.current || undefined,
      host_origin: hostOrigin || undefined,
    };
    const url = `${API_BASE_URL}/panel-history/turns`;

    if (unloading) {
      // The document is going away, so this has to survive it. `keepalive`
      // carries proper CORS and headers; sendBeacon is the fallback for engines
      // that will not take a keepalive fetch.
      const body = JSON.stringify(payload);
      try {
        await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body,
          keepalive: true,
        });
      } catch {
        try {
          navigator.sendBeacon?.(url, new Blob([body], { type: 'application/json' }));
        } catch { /* nothing further to try; the transcript is simply not kept */ }
      }
      return null;
    }

    if (flushingRef.current) return null;
    flushingRef.current = true;
    try {
      const response = await api.post('/panel-history/turns', payload);
      if (response.data?.conversation_id) {
        conversationIdRef.current = response.data.conversation_id;
        // The thread now lives in the new row, which carries the old turns
        // forward. Clearing this stops a later flush from retiring the parent
        // a second time.
        resumeFromRef.current = null;
      }
      return response.data;
    } catch {
      return null; // history is never allowed to disturb a live conversation
    } finally {
      flushingRef.current = false;
    }
  }, [historyAvailable, agent, visitorId, hostOrigin]);

  // Mirror captions and the session id for the callbacks above, and mark the
  // row stale. Both are read during page unload, where a render closure from
  // the last commit would be reaching for values that have since moved on.
  useEffect(() => {
    captionsRef.current = captions;
    if (captions.length > 0) flushDirtyRef.current = true;
  }, [captions]);

  useEffect(() => { sessionIdRef.current = sessionId; }, [sessionId]);

  useEffect(() => {
    if (!historyAvailable || !isConnected) return undefined;
    const id = setInterval(() => {
      if (!flushDirtyRef.current) return;
      flushDirtyRef.current = false;
      flushHistory();
    }, HISTORY_FLUSH_MS);
    return () => clearInterval(id);
  }, [historyAvailable, isConnected, flushHistory]);

  // Closing the tab is the most common way a conversation ends, and it never
  // reaches the End button. `pagehide` fires in cases `beforeunload` does not,
  // notably a mobile browser moving the page into the back/forward cache.
  useEffect(() => {
    if (!historyAvailable) return undefined;
    const onHide = () => {
      if (captionsRef.current.length > 0) flushHistory({ unloading: true });
    };
    window.addEventListener('pagehide', onHide);
    return () => window.removeEventListener('pagehide', onHide);
  }, [historyAvailable, flushHistory]);

  const loadHistoryList = useCallback(async () => {
    if (!historyAvailable) return;
    setHistoryLoading(true);
    try {
      const response = await api.get('/panel-history/conversations', {
        params: { agent_id: agent.id, visitor_id: visitorId, host_origin: hostOrigin || undefined },
      });
      const rows = Array.isArray(response.data) ? response.data : [];
      // The conversation being recorded right now is on screen already; showing
      // it in the list as something to "continue" would be nonsense.
      setHistoryList(rows.filter((row) => row.id !== conversationIdRef.current));
    } catch {
      setHistoryList([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [historyAvailable, agent, visitorId, hostOrigin]);

  const openHistory = useCallback(() => {
    setHistoryDetail(null);
    setPane('history');
    loadHistoryList();
  }, [loadHistoryList]);

  const openHistoryDetail = useCallback(async (id) => {
    setHistoryBusy(true);
    try {
      const response = await api.get(`/panel-history/conversations/${id}`, {
        params: { visitor_id: visitorId },
      });
      setHistoryDetail(response.data);
      setPane('historyDetail');
    } catch {
      // Gone or lapsed while the list was on screen: refresh rather than open
      // an empty transcript.
      loadHistoryList();
    } finally {
      setHistoryBusy(false);
    }
  }, [visitorId, loadHistoryList]);

  const deleteHistoryEntry = useCallback(async (id) => {
    setHistoryList((prev) => prev.filter((row) => row.id !== id));
    try {
      await api.delete(`/panel-history/conversations/${id}`, {
        params: { visitor_id: visitorId },
      });
    } catch {
      loadHistoryList(); // put it back if the delete did not take
    }
  }, [visitorId, loadHistoryList]);

  const clearHistory = useCallback(async () => {
    if (!historyAvailable) return;
    setHistoryList([]);
    try {
      await api.delete('/panel-history/conversations', {
        params: { agent_id: agent.id, visitor_id: visitorId },
      });
    } catch {
      loadHistoryList();
    }
  }, [historyAvailable, agent, visitorId, loadHistoryList]);

  // ── Session control ────────────────────────────────────────────────────────

  // Connect to a room and wire up everything the panel listens to. Shared by a
  // new session and by a co-browse call picked up after a page load.
  const joinRoom = useCallback(async (url, token, { micOn, withCobrowse = false }) => {
    const newRoom = new Room({
      adaptiveStream: true,
      dynacast: true,
      disconnectOnPageLeave: false,
      videoCaptureDefaults: { resolution: { width: 640, height: 360 } },
      defaultAudioCaptureOptions: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });

    const subscribeToAvatarVideo = (participant) => {
      if (avatarHidden || !isAvatarParticipant(participant)) return;
      participant.videoTrackPublications.forEach((publication) => {
        if (!publication.track) return;
        try {
          publication.setVideoQuality(VideoQuality.LOW);
          publication.setVideoFPS(30);
        } catch { /* quality hints are best effort */ }
        setAvatarVideoTrack(publication.track);
        attachVideoTrack(publication.track);
      });
    };

    newRoom.on(RoomEvent.Connected, () => {
      setIsConnected(true);
      setIsConnecting(false);
      newRoom.remoteParticipants.forEach(subscribeToAvatarVideo);
    });

    newRoom.on(RoomEvent.Disconnected, () => {
      videoAttachedRef.current = false;
      avatarReadyNotifiedRef.current = false;
      setIsConnected(false);
      setAvatarVideoTrack(null);
    });

    newRoom.on(RoomEvent.ParticipantConnected, subscribeToAvatarVideo);

    newRoom.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
      if (track.kind === 'video' && !avatarHidden && isAvatarParticipant(participant)) {
        try {
          publication.setVideoQuality(VideoQuality.LOW);
          publication.setVideoFPS(30);
        } catch { /* quality hints are best effort */ }
        setAvatarVideoTrack(track);
        if (!attachVideoTrack(track)) {
          setTimeout(() => videoRef.current && track && attachVideoTrack(track), 500);
        }
      } else if (track.kind === 'audio') {
        if (audioRef.current) track.attach(audioRef.current);
        // The transcript is paced against the avatar's voice. Another audio
        // track subscribed after it (the order changes when a co-browse call
        // rejoins after a page load) must not take the meter over, or the
        // text waits on a track that never speaks and lands after the speech.
        const fromAvatar = isAvatarParticipant(participant);
        if (fromAvatar || !analyserFromAvatarRef.current) {
          const made = makeAnalyser(track);
          if (made) {
            analyserRef.current = made.analyser;
            analyserDataRef.current = made.data;
            analyserFromAvatarRef.current = fromAvatar;
          }
        }
      }
    });

    newRoom.on(RoomEvent.TrackUnsubscribed, (track, publication, participant) => {
      if (track.kind === 'video' && isAvatarParticipant(participant)) {
        videoAttachedRef.current = false;
        avatarReadyNotifiedRef.current = false;
        setAvatarVideoTrack(null);
        if (videoRef.current) track.detach(videoRef.current);
      }
    });

    newRoom.on(RoomEvent.LocalTrackPublished, (publication) => {
      if (publication.kind !== 'audio' || !publication.track) return;
      const made = makeAnalyser(publication.track);
      if (made) {
        micAnalyserRef.current = made.analyser;
        micDataRef.current = made.data;
      }
    });

    if (typeof newRoom.registerTextStreamHandler === 'function') {
      try {
        newRoom.registerTextStreamHandler(TRANSCRIPTION_TOPIC, async (reader, participantInfo) => {
          const identity = participantInfo?.identity || '';
          const localIdentity = newRoom.localParticipant?.identity || LOCAL_IDENTITY;
          const speaker = identity === localIdentity ? 'user' : 'ai';
          const segmentId = reader.info?.attributes?.[SEGMENT_ID_ATTR] || reader.info?.id;
          const key = `segment-${speaker}-${segmentId}`;
          let buffer = '';
          // Stamped before a single word is read: the reveal loop needs to
          // know the tail of the turn now playing was never spoken.
          if (speaker === 'ai') setAgentThinking(true);
          else markBargeIn();
          try {
            for await (const chunk of reader) {
              buffer += chunk;
              // The visitor's own words are already spoken, so show them
              // straight away. The agent's are queued and released in step
              // with the avatar's audio.
              if (speaker === 'user') upsertCaption(key, speaker, buffer);
              else pushAgentText(key, buffer, false);
            }
          } catch { /* stream ended early; whatever arrived is kept */ }
          if (speaker === 'ai') pushAgentText(key, buffer, true);
        });
      } catch { /* a handler is already registered on this room */ }
    }

    newRoom.on(RoomEvent.DataReceived, (payload) => {
      try {
        const data = JSON.parse(new TextDecoder().decode(payload));
        if (data.type === 'transcription' || data.type === 'conversation_item') {
          const speaker = data.speaker === 'user' ? 'user' : 'ai';
          // `interrupted` cannot be read as the truth about this turn. The
          // avatar owns the audio output and never reports playback finished,
          // so the framework holds the speech handle open and the flag ends up
          // set on *every* assistant turn - it fires when the next visitor turn
          // cancels the handle, whether or not the avatar had already finished
          // speaking. That timing is still worth having: it is the earliest
          // notice the panel gets that the visitor has started talking, ahead
          // of their first interim transcript, and it is the only notice at all
          // when a cough or a noise interrupts the agent and STT returns
          // nothing. Whether any words were left unspoken is decided where it
          // always was - against the avatar's own audio.
          if (speaker === 'ai' && data.interrupted) markBargeIn();
          addCaption(speaker, data.text, data.images?.length ? chatImagesFromIds(agentId, data.images) : undefined);
        }
      } catch { /* not a transcript frame */ }
    });

    // Co-browse commands, page loads and hang-up (useCobrowseBridge.js)
    if (withCobrowse) cobrowseRef.current.bindRoom(newRoom);

    await newRoom.connect(url, token);
    setRoom(newRoom);

    resetRevealState(revealRef.current);
    if (revealTimerRef.current) clearInterval(revealTimerRef.current);
    revealTimerRef.current = setInterval(tickReveal, REVEAL_TICK_MS);

    try {
      await newRoom.localParticipant.setMicrophoneEnabled(micOn);
    } catch (micError) {
      if (withCobrowse) {
        // A co-browse call would otherwise sit there with nobody able to talk,
        // and the agent would hold it for page loads. End it properly.
        await cobrowseRef.current.hangup();
        await newRoom.disconnect().catch(() => {});
      }
      throw micError;
    }
    setMicEnabled(micOn);
    return newRoom;
  }, [attachVideoTrack, makeAnalyser, tickReveal, upsertCaption, pushAgentText, addCaption, markBargeIn, avatarHidden, agentId]);

  const startSession = useCallback(async () => {
    if (!agent || startedRef.current) return;
    startedRef.current = true;
    let cobrowseSessionId = null;
    try {
      setIsConnecting(true);
      setError(null);

      const variablesToSend = {};
      dynamicVariableNames.forEach((name) => {
        const value = dynamicVariableValues[name];
        if (value && value.trim()) variablesToSend[name] = value.trim();
      });
      const sessionOptions = {
        ...(Object.keys(variablesToSend).length > 0 && { variables: variablesToSend }),
        // Resume sends an id, never the transcript. The backend rebuilds the
        // context from its own record, so nothing that passed through this
        // browser can reach the agent's system prompt. A stale or foreign id is
        // ignored server-side and the session simply starts fresh.
        ...(historyAvailable && visitorId && { visitor_id: visitorId }),
        ...(historyAvailable && resumeFromRef.current && { resume_from: resumeFromRef.current }),
        // Tells the worker not to start a LiveAvatar session at all. Without it
        // the avatar would still be spun up and paid for behind a panel that
        // never shows it, and the greeting would sit waiting on a video frame
        // this browser is never going to confirm.
        ...(avatarHidden && { hide_avatar: true }),
      };

      // Co-browse when the page engine answered and the agent allows this site;
      // otherwise the ordinary panel session, exactly as before.
      const withCobrowse = cobrowseRef.current.requested && await cobrowseRef.current.whenHostReady();
      let url;
      let token;
      if (withCobrowse) {
        const started = await cobrowseRef.current.start(sessionOptions);
        cobrowseSessionId = started.session_id;
        ({ url, token } = started);
        setSessionId(started.session_id);
        sessionIdRef.current = started.session_id;
      } else {
        const roomName = `test-agent-${agent.id}-${Date.now()}`;
        const sessionResponse = await api.post('/sessions/test', {
          room_name: roomName,
          agent_id: agent.id,
          channel: 'test',
          ...sessionOptions,
        });
        setSessionId(sessionResponse.data.session_id);
        sessionIdRef.current = sessionResponse.data.session_id;

        const tokenResponse = await api.post('/token', { room: roomName, identity: LOCAL_IDENTITY });
        ({ url, token } = tokenResponse.data);
      }

      clockStartRef.current = Date.now();
      const newRoom = await joinRoom(url, token, { micOn: !settings.startMuted, withCobrowse });
      if (withCobrowse) cobrowseRef.current.afterConnect(newRoom, { pillSide: cobrowsePillSide });
    } catch (err) {
      startedRef.current = false;
      if (cobrowseSessionId) {
        // The call was set up but never joined: close it rather than leave the
        // agent holding a room nobody is coming to.
        cobrowseRef.current.clearRecord();
        api.post(`/sessions/${cobrowseSessionId}/end`).catch(() => {});
      }
      setError(err.response?.data?.detail || err.message || 'Could not start the conversation');
      setIsConnecting(false);
    }
  }, [
    agent, dynamicVariableNames, dynamicVariableValues, joinRoom, settings.startMuted,
    historyAvailable, visitorId, avatarHidden, cobrowsePillSide,
  ]);

  /**
   * Pick up a co-browse call the previous page released (useCobrowseBridge.js).
   * The agent is holding it, the LiveAvatar is still in the room, and the
   * transcript comes from the host page's call record.
   */
  const pendingCobrowseResumeRef = useRef(null);
  const resumeCobrowse = useCallback(async (record) => {
    const bridge = cobrowseRef.current;
    if (!bridge.canResume(record) || startedRef.current) return;
    if (!agent) {
      pendingCobrowseResumeRef.current = record; // replayed once the agent loads
      return;
    }
    startedRef.current = true;
    const seeded = (Array.isArray(record.captions) ? record.captions : [])
      .filter((c) => c && c.text)
      .map((c, index) => ({
        key: c.key || `carried-${index}`,
        speaker: c.speaker === 'user' ? 'user' : 'ai',
        text: c.text,
        at: c.at || 0,
        updatedAt: c.at || 0,
        cut: !!c.cut,
      }));
    setCaptions(seeded);
    captionsRef.current = seeded;
    conversationIdRef.current = record.conversationId || null;
    clockStartRef.current = record.startedAt || Date.now();
    setSessionId(record.sessionId);
    sessionIdRef.current = record.sessionId;
    setError(null);
    setIsConnecting(true);
    setPane('live');
    if (!alwaysOpen) setMode(record.mode === 'mini' ? 'mini' : 'open');

    try {
      const joined = await bridge.rejoin(record);
      const newRoom = await joinRoom(joined.url, joined.token, {
        micOn: record.micEnabled !== false,
        withCobrowse: true,
      });
      // LiveKit creates a room on join, so one that stays empty means the call
      // ended while the page was loading.
      if (!(await waitForParticipant(newRoom))) {
        await newRoom.disconnect().catch(() => {});
        throw new Error('ended');
      }
      bridge.afterConnect(newRoom, { pillSide: cobrowsePillSide });
      if (record.mode === 'pip' && pipAvailable) setMode('pip');
    } catch (err) {
      // Why, for whoever is debugging a dropped call (status 410 = the call had
      // already ended on the server, "ended" = nobody in the room)
      console.warn('[BrainCX co-browse] could not continue the conversation:', err?.response?.status || '', err?.response?.data?.detail || err?.message);
      bridge.clearRecord();
      stopReveal();
      startedRef.current = false;
      clockStartRef.current = null;
      setRoom(null);
      setIsConnected(false);
      setIsConnecting(false);
      setSessionId(null);
      sessionIdRef.current = null;
      setAvatarVideoTrack(null);
      setError(
        err?.name === 'NotAllowedError'
          ? 'Microphone access was blocked on this page. Allow it, then start a new conversation.'
          : 'Your conversation ended while the page was loading. You can start a new one.'
      );
      if (!alwaysOpen) setMode('open');
    }
  }, [agent, alwaysOpen, joinRoom, cobrowsePillSide, pipAvailable, stopReveal]);
  resumeCobrowseRef.current = resumeCobrowse;

  useEffect(() => {
    if (!agent || !pendingCobrowseResumeRef.current) return;
    const record = pendingCobrowseResumeRef.current;
    pendingCobrowseResumeRef.current = null;
    resumeCobrowse(record);
  }, [agent, resumeCobrowse]);

  const stopSession = useCallback(async () => {
    // Persist before tearing anything down: the finally block clears the
    // captions this reads from.
    if (historyAvailable && captionsRef.current.length > 0) {
      flushDirtyRef.current = false;
      const stored = await flushHistory();
      const closedId = stored?.conversation_id || conversationIdRef.current;
      if (closedId) {
        // Tells the backend the thread is finished, which is what schedules the
        // summary for a long one. Fire and forget - a missed summary only means
        // a later resume replays the tail instead of a digest.
        api.post(`/panel-history/conversations/${closedId}/close`, null, {
          params: { visitor_id: visitorId },
        }).catch(() => {});
      }
    }
    try {
      // A co-browse hang-up is announced first, so the agent ends the call at
      // once instead of holding it for a page load.
      if (cobrowseRef.current.hasCall()) await cobrowseRef.current.hangup();
      if (room) {
        await room.disconnect();
        setRoom(null);
      }
      if (sessionId) await api.post(`/sessions/${sessionId}/end`);
    } catch { /* the session is going away either way */ } finally {
      stopReveal();
      startedRef.current = false;
      clockStartRef.current = null;
      videoAttachedRef.current = false;
      avatarReadyNotifiedRef.current = false;
      setIsConnected(false);
      setIsConnecting(false);
      setSessionId(null);
      sessionIdRef.current = null;
      setCaptions([]);
      captionsRef.current = [];
      conversationIdRef.current = null;
      resumeFromRef.current = null;
      setAvatarVideoTrack(null);
      setAgentThinking(false);
    }
  }, [room, sessionId, stopReveal, historyAvailable, flushHistory, visitorId]);

  /**
   * Continue an earlier conversation.
   *
   * The old turns are seeded into the transcript so the visitor can see the
   * thread they picked up, and because the flush sends whatever is on screen,
   * that also carries the thread forward into the new row. The backend retires
   * the old row once the new one has it, so History keeps showing one entry per
   * conversation rather than a chain of fragments.
   */
  const resumeConversation = useCallback(async (conversation) => {
    if (!conversation || !Array.isArray(conversation.turns) || conversation.turns.length === 0) return;
    setHistoryBusy(true);
    try {
      // Ends whatever is running first: stopSession clears the transcript, so
      // seeding before it would be undone.
      if (startedRef.current) await stopSession();

      // A stored turn with no `at` must not be stamped with the clock as it is
      // read back: that would place an old sentence after everything said in
      // this session once the webhook renders the thread as elapsed time.
      let lastAt = 0;
      const seeded = conversation.turns.map((turn, index) => {
        const at = typeof turn.at === 'number' ? turn.at : lastAt + 1;
        lastAt = at;
        return {
          key: `earlier-${conversation.id}-${index}`,
          speaker: turn.speaker === 'user' ? 'user' : 'ai',
          text: turn.text,
          at,
          earlier: true,
        };
      });
      setCaptions(seeded);
      captionsRef.current = seeded;
      conversationIdRef.current = null;
      resumeFromRef.current = conversation.id;

      setHistoryDetail(null);
      setPane('live');
      await startSession();
    } finally {
      setHistoryBusy(false);
    }
  }, [stopSession, startSession]);

  const sendText = useCallback(async (raw) => {
    const message = (raw ?? textValue).trim();
    const withImages = raw === undefined && imagesEnabled && attachments.readyCount > 0;
    if ((!message && !withImages) || attachments.uploading || !room?.localParticipant || sending) return;
    let images = [];
    try {
      setSending(true);
      // The agent treats an incoming chat message as a user turn: it interrupts
      // whatever is being spoken. Same barge-in as talking over it.
      markBargeIn();
      if (withImages) {
        // Images need a richer turn than lk.chat can carry, so they go on their
        // own topic (app/agent/chat_image_input.py). The echo uses the same
        // text the agent records, so the two copies collapse into one bubble.
        images = attachments.take();
        const ids = images.map((img) => img.id);
        await room.localParticipant.sendText(JSON.stringify({ text: message, images: ids }), { topic: CHAT_IMAGE_TOPIC });
        addCaption('user', imageTranscriptText(message, ids), images);
      } else {
        await room.localParticipant.sendText(message, { topic: CHAT_TOPIC });
        addCaption('user', message); // optimistic echo; the backend also publishes the turn
      }
      setAgentThinking(true);
      if (raw === undefined) setTextValue('');
    } catch {
      attachments.restore(images);
      setError('Could not send that message. Please try again.');
    } finally {
      setSending(false);
    }
  }, [textValue, room, sending, addCaption, markBargeIn, imagesEnabled, attachments]);

  const toggleMic = useCallback(async () => {
    if (!room?.localParticipant) return;
    const next = !micEnabled;
    try {
      await room.localParticipant.setMicrophoneEnabled(next);
      setMicEnabled(next);
    } catch { /* the browser refused the change; leave the UI as it was */ }
  }, [room, micEnabled]);

  useEffect(() => () => { if (room) room.disconnect(); }, [room]);
  useEffect(() => () => {
    stopReveal();
    audioCtxRef.current?.close().catch(() => {});
    audioCtxRef.current = null;
  }, [stopReveal]);

  // ── Open / minimize / close ────────────────────────────────────────────────

  const open = useCallback(() => {
    setTeaserVisible(false);
    setMode('open');
    if (settings.autoStart && !startedRef.current && agent) startSession();
  }, [settings.autoStart, agent, startSession]);

  const minimize = useCallback(() => {
    // Only worth a pill if there is something live to come back to.
    setMode(hasSession ? 'mini' : 'closed');
  }, [hasSession]);

  // ── Picture in picture ─────────────────────────────────────────────────────
  // Same contract as minimizing: the chrome goes, the session stays. The stage
  // node is a sibling of the chrome and is never unmounted, so the video keeps
  // playing on the same track through the morph and the transcript is still
  // there when the card is expanded back.

  const enterPip = useCallback(() => {
    if (!pipAvailable || !hasSession) return;
    setPane('live');
    setMode('pip');
  }, [pipAvailable, hasSession]);

  const exitPip = useCallback(() => {
    setMode('open');
  }, []);

  // A session that ends while the card is floating has nothing left to float.
  // Expanding is the recoverable state - the panel shows its start screen again
  // rather than leaving a dead video parked on the visitor's page. Ending the
  // call from the card itself goes to `closed` first, so this does not fight it.
  useEffect(() => {
    if (mode === 'pip' && !hasSession) setMode('open');
  }, [mode, hasSession]);

  // ── Co-browse call record ──────────────────────────────────────────────────
  // The host page keeps the live call so the panel on the next page can carry
  // it on: transcript, mode, microphone and history thread. Captions change
  // many times a second while a reply is revealed, so they are saved at most
  // once a second, with the full text of a reply still being revealed.
  const cobrowseSaveRef = useRef({ timer: null, last: 0 });
  const saveCobrowseCaptions = useCallback(() => {
    const bridge = cobrowseRef.current;
    if (!bridge.hasCall()) return;
    const segments = revealRef.current.segments;
    const saved = captionsRef.current.slice(-COBROWSE_SAVED_CAPTIONS).map((c) => {
      const pending = c.speaker === 'ai' && !c.cut ? segments.find((seg) => seg.key === c.key) : null;
      return {
        key: c.key,
        speaker: c.speaker,
        text: pending ? pending.full : c.text,
        at: c.at,
        ...(c.cut && { cut: true }),
      };
    });
    bridge.saveRecord({ captions: saved, conversationId: conversationIdRef.current || null });
  }, []);

  useEffect(() => {
    if (!cobrowseRef.current.hasCall()) return;
    const state = cobrowseSaveRef.current;
    if (state.timer) return; // already scheduled, and it reads the latest captions
    const wait = Math.max(0, COBROWSE_SAVE_INTERVAL_MS - (Date.now() - state.last));
    state.timer = setTimeout(() => {
      state.timer = null;
      state.last = Date.now();
      saveCobrowseCaptions();
    }, wait);
  }, [captions, saveCobrowseCaptions]);

  useEffect(() => () => clearTimeout(cobrowseSaveRef.current.timer), []);

  useEffect(() => {
    if (!isConnected || !cobrowseRef.current.hasCall()) return;
    cobrowseRef.current.saveRecord({ mode, micEnabled, conversationId: conversationIdRef.current || null });
  }, [mode, micEnabled, isConnected]);

  useEffect(() => {
    if (isConnected) cobrowseRef.current.setPillSide(cobrowsePillSide);
  }, [cobrowsePillSide, isConnected]);

  // After a page load the browser can hold the avatar's audio until the visitor
  // interacts with this page. The button for that lives in the open panel.
  useEffect(() => {
    if (cobrowse.audioBlocked && isConnected && !alwaysOpen && (mode === 'mini' || mode === 'pip')) {
      setMode('open');
    }
  }, [cobrowse.audioBlocked, isConnected, alwaysOpen, mode]);

  const close = useCallback(() => {
    if (hasSession) stopSession();
    setMode('closed');
    // Reopening is a fresh start, so it should not land the visitor back in a
    // history pane they left open a session ago.
    setPane('live');
    setHistoryDetail(null);
  }, [hasSession, stopSession]);

  const toggle = useCallback(() => {
    setMode((m) => (m === 'open' ? (hasSession ? 'mini' : 'closed') : 'open'));
  }, [hasSession]);

  // Escape collapses rather than hangs up: the visitor keeps their session.
  useEffect(() => {
    if (mode !== 'open' && mode !== 'pip') return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      // From PIP, Escape puts the card back into the panel. It never hangs up.
      if (mode === 'pip') {
        exitPip();
        return;
      }
      // Inside history, Escape steps back one level rather than collapsing the
      // whole panel - the visitor is navigating, not trying to leave.
      if (pane === 'historyDetail') setPane('history');
      else if (pane === 'history') setPane('live');
      else if (!alwaysOpen) minimize();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mode, alwaysOpen, minimize, exitPip, pane]);

  // Proactive greeting bubble, once per tab.
  useEffect(() => {
    if (viewMode !== 'launcher' || !settings.teaserEnabled || mode !== 'closed') return undefined;
    let dismissed = false;
    try {
      dismissed = window.sessionStorage.getItem('bx-avatar-panel-teaser') === '1';
    } catch { /* storage blocked; show it */ }
    if (dismissed) return undefined;
    const id = setTimeout(() => setTeaserVisible(true), Math.max(0, settings.teaserDelay));
    return () => clearTimeout(id);
  }, [viewMode, settings.teaserEnabled, settings.teaserDelay, mode]);

  const dismissTeaser = useCallback(() => {
    setTeaserVisible(false);
    try { window.sessionStorage.setItem('bx-avatar-panel-teaser', '1'); } catch { /* ignore */ }
  }, []);

  // ── Loader handshake ───────────────────────────────────────────────────────

  const postToLoader = useCallback((payload) => {
    if (typeof window === 'undefined' || window.parent === window) return;
    try { window.parent.postMessage(payload, '*'); } catch { /* cross-origin parent */ }
  }, []);

  // ── Dragging the PIP card ──────────────────────────────────────────────────
  // The card the visitor grabs lives in this iframe, but the thing that has to
  // move is the iframe, so the pointer is forwarded to the loader. Two details
  // are what make it hold together across the frame boundary:
  //
  //   * The grab offset is measured against the card, so every move recomputes
  //     an absolute position from the current pointer. Nothing accumulates, so a
  //     coalesced or dropped message cannot leave the card drifting behind the
  //     cursor - the next move it hears about puts it exactly right.
  //   * Pointer capture keeps the moves arriving here even while the pointer
  //     runs ahead of the frame and briefly leaves it. The loader watches its own
  //     document for the same pointer as well, so either side alone is enough to
  //     keep the drag alive.

  const pipDragMove = useCallback((clientX, clientY, screenX, screenY) => {
    const drag = pipDragRef.current;
    if (!drag) return;
    if (usesLoader) {
      // Relayed for every pointer type. Chrome keeps routing a mouse drag to the
      // frame it started in, so the loader cannot rely on hearing it natively.
      // Screen coordinates, not frame ones. A frame coordinate is measured
      // against wherever the frame was last painted, which is a frame behind the
      // position the loader last wrote, so converting it back with the newer
      // position overshoots by one step and the next move pulls it back: the
      // card shakes frame by frame. The screen does not move with the frame.
      postToLoader({ type: MSG_DRAG, phase: 'move', x: clientX, y: clientY, sx: screenX, sy: screenY });
      return;
    }
    // Standalone: this document is the page, so the card places itself.
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    setPipPos({
      left: Math.min(Math.max(0, clientX - drag.grabX), Math.max(0, vw - pipSize.width)),
      top: Math.min(Math.max(0, clientY - drag.grabY), Math.max(0, vh - pipSize.height)),
    });
  }, [usesLoader, postToLoader, pipSize.width, pipSize.height]);

  const onPipPointerDown = useCallback((event) => {
    if (mode !== 'pip') return;
    // Controls on the card keep their clicks.
    if (event.target?.closest?.('[data-bx-nodrag]')) return;
    if (typeof event.button === 'number' && event.button !== 0) return;
    const node = event.currentTarget;
    const rect = node.getBoundingClientRect();
    const grabX = event.clientX - rect.left;
    const grabY = event.clientY - rect.top;
    pipDragRef.current = { grabX, grabY, pointerId: event.pointerId, node };
    try { node.setPointerCapture(event.pointerId); } catch { /* older engine; the loader still tracks it */ }
    event.preventDefault(); // no text selection, no native drag of the video
    setPipDragging(true);
    // The pointer type goes with it because the loader takes the whole drag over
    // for a mouse - it makes the frame pointer-transparent so the moves reach it
    // natively instead of one postMessage per move - and cannot do that for a
    // touch, whose target is fixed for the life of the gesture.
    if (usesLoader) {
      postToLoader({
        type: MSG_DRAG,
        phase: 'start',
        grabX,
        grabY,
        pointerType: event.pointerType,
        // The card is still at rest, so this pair is exact and lets the loader
        // tie screen coordinates to page ones for the rest of the drag.
        x: event.clientX,
        y: event.clientY,
        sx: event.screenX,
        sy: event.screenY,
      });
    }
  }, [mode, usesLoader, postToLoader]);

  const onPipPointerMove = useCallback((event) => {
    if (!pipDragRef.current) return;
    pipDragMove(event.clientX, event.clientY, event.screenX, event.screenY);
  }, [pipDragMove]);

  // `silent` is the drag the loader finished on its own: it owned the pointer, so
  // it already knows, and telling it again would be an echo.
  const endPipDrag = useCallback((silent) => {
    const drag = pipDragRef.current;
    if (!drag) return;
    pipDragRef.current = null;
    setPipDragging(false);
    try { drag.node?.releasePointerCapture?.(drag.pointerId); } catch { /* already released */ }
    if (usesLoader && !silent) postToLoader({ type: MSG_DRAG, phase: 'end' });
  }, [usesLoader, postToLoader]);

  // Leaving PIP mid-drag (a command from the host page, a session that ended)
  // would otherwise leave the loader waiting for a pointerup that never comes.
  useEffect(() => {
    if (mode !== 'pip') endPipDrag();
  }, [mode, endPipDrag]);

  useEffect(() => {
    if (!usesLoader) return undefined;
    const onMessage = (event) => {
      if (event.source !== window.parent) return;
      const data = event.data;
      if (!data || typeof data !== 'object') return;
      if (data.type === MSG_COMMAND) {
        if (data.action === 'open') open();
        else if (data.action === 'minimize') minimize();
        else if (data.action === 'close') close();
        else if (data.action === 'toggle') toggle();
        else if (data.action === 'pip') enterPip();
      } else if (data.type === MSG_VIEWPORT) {
        // Kept for internal decisions that depend on the host viewport rather
        // than this iframe's own box.
        if (typeof data.vw === 'number') {
          setHostViewport((prev) =>
            prev.vw === data.vw && prev.vh === data.vh ? prev : { vw: data.vw, vh: data.vh }
          );
        }
      } else if (data.type === MSG_DRAG && data.phase === 'end') {
        // The loader ran this drag itself, with the frame transparent to the
        // pointer, so this document never saw the pointerup.
        endPipDrag(true);
      } else if (data.type === MSG_INSET && data.inset) {
        const next = data.inset;
        setInsets((prev) =>
          prev.l === next.l && prev.t === next.t && prev.r === next.r && prev.b === next.b
            ? prev
            : { l: next.l, t: next.t, r: next.r, b: next.b }
        );
      }
    };
    window.addEventListener('message', onMessage);
    postToLoader({ type: MSG_READY });
    return () => window.removeEventListener('message', onMessage);
  }, [usesLoader, open, minimize, close, toggle, enterPip, endPipDrag, postToLoader]);

  useEffect(() => {
    if (!usesLoader) return;
    postToLoader({
      type: MSG_STATE,
      mode,
      hasSession,
      variant: settings.variant,
      side: settings.side,
      drawerEdge: settings.drawerEdge,
      position: settings.position,
      panelWidth: settings.panelWidth,
      spotlightWidth: settings.spotlightWidth,
      spotlightHeight: settings.spotlightHeight,
      // The size the card actually wants, already through the scale mode and
      // the host viewport. The loader positions it from the anchor rather than
      // recomputing any of that.
      spotlightW: spotlightBox.width,
      spotlightH: spotlightBox.height,
      spotlightAnchor: settings.spotlightAnchor,
      mobileMode: settings.mobileMode,
      radius: theme.radius,
      // The PIP card is not content-sized, so unlike the launcher it needs no
      // measurement round trip: the size is arithmetic on the settings and it
      // travels with the mode that uses it.
      pipWidth: pipSize.width,
      pipHeight: pipSize.height,
    });
  }, [usesLoader, mode, hasSession, settings, theme.radius, pipSize, spotlightBox, postToLoader]);

  // The launcher and the mini pill are content-sized; the loader cannot guess
  // how wide a label or a teaser bubble makes them, so they are measured here.
  useEffect(() => {
    if (!usesLoader || !launcherNode || (mode !== 'closed' && mode !== 'mini')) return undefined;
    const send = () => {
      // getBoundingClientRect, not offsetWidth: offsetWidth rounds, and a width
      // rounded down is a width the content then overflows by a fraction.
      const rect = launcherNode.getBoundingClientRect();
      postToLoader({
        type: MSG_MEASURE,
        mode,
        width: Math.ceil(rect.width || launcherNode.offsetWidth),
        height: Math.ceil(rect.height || launcherNode.offsetHeight),
      });
    };
    send();
    // The observer covers every later size change - the greeting bubble
    // appearing, a label edit, a font finishing loading - so none of those need
    // to be listed as dependencies here.
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(send);
    observer.observe(launcherNode);
    return () => observer.disconnect();
  }, [usesLoader, launcherNode, mode, postToLoader]);

  // ── Geometry ───────────────────────────────────────────────────────────────
  // One source of truth for the absolute layout. The stage is a sibling of the
  // chrome rather than a child, so it survives the switch between the open
  // panel and the mini pill and can morph between the two.

  const PAD = 10;
  const HEADER_H = PANEL_HEADER_H;
  const PIP_RADIUS = 16;
  const isSpotlight = settings.variant === 'spotlight';
  // The visible widget is the iframe minus the loader's shadow gutter. Every
  // measurement below works on that box, never on the raw iframe.
  const contentW = Math.max(1, hostSize.w - insets.l - insets.r);
  const contentH = Math.max(1, hostSize.h - insets.t - insets.b);
  // A spotlight the client asked to stack: video across the top, conversation
  // under it. Only meaningful for a spotlight - a drawer is a single column by
  // definition and already stacks.
  //
  // Off on a phone, where the card is a full width sheet that already stacks and
  // has no room to give the video a bigger share. There it keeps the band rules
  // below, which is what a phone has always got.
  const stackedByChoice = isSpotlight
    && settings.spotlightLayout === 'stacked'
    && hostViewport.vw >= MOBILE_MAX;
  // Splitting a spotlight into two columns is a way of making room for the
  // video. With no video it would just be a narrow transcript beside a hole,
  // and under 660px there is not enough width for two readable columns - so a
  // narrow card stacks whether or not anyone asked it to.
  const split = mode === 'open' && isSpotlight && contentW >= 660
    && !avatarHidden && !stackedByChoice;
  // Deliberately measured against the host page, not `contentW`. In launcher
  // mode the iframe is sized to the launcher, so deciding the launcher's shape
  // from the iframe width is circular: a pill would shrink the frame, the
  // narrow frame would collapse it to a circle, and it would stay a circle.
  const narrowViewport = hostViewport.vw < 400;

  const geometry = useMemo(() => {
    // No avatar: there is no stage to place, and the body takes the whole panel
    // under the header. The mini pill keeps its shape - the brand mark is drawn
    // inside it rather than as the sibling stage.
    if (avatarHidden) {
      return { stage: null, body: { left: 0, top: HEADER_H, right: 0, bottom: 0 } };
    }
    // The stage is a sibling of the shell, so its coordinates are relative to
    // the whole iframe and have to carry the gutter. Everything inside `body`
    // lives in the shell, which is already inset.
    if (mode === 'mini') {
      return {
        stage: { left: insets.l + 8, top: insets.t + 8, width: 52, height: 52, radius: 26 },
        body: null,
      };
    }
    // PIP: the stage *is* the widget. It fills the box the loader gave it, which
    // is the card size this component asked for, so there is no chrome to lay
    // out around it.
    if (mode === 'pip') {
      return {
        stage: { left: insets.l, top: insets.t, width: contentW, height: contentH, radius: PIP_RADIUS },
        body: null,
      };
    }
    const w = Math.max(240, contentW);
    const h = Math.max(320, contentH);
    if (split) {
      const stageW = Math.round(Math.min(w * 0.5, w - 320));
      return {
        stage: {
          left: insets.l + PAD,
          top: insets.t + HEADER_H,
          width: stageW,
          height: h - HEADER_H - PAD,
          radius: 16,
        },
        body: { left: stageW + PAD * 2, top: HEADER_H, right: 0, bottom: 0 },
      };
    }
    // Stack: the stage is a band under the header. Two different things land
    // here. A drawer, or a card too narrow to split, gets a *band* - capped at
    // 300px so the transcript and the composer always keep their room, because
    // an uncapped 16:9 video eats a short window whole and pushes the send
    // button off screen. A spotlight that was asked to stack gets a real video
    // area instead: the client picked that shape because the video is the point
    // of it, so the cap comes off and the share of the height is theirs.
    const idle = !isConnected && !isConnecting;
    const stageH = stackedByChoice
      ? avatarSpotlightStageHeight(settings, h)
      : Math.max(150, Math.min(
        Math.round(h * (idle ? 0.34 : 0.4)), 300, h - HEADER_H - 210
      ));
    return {
      stage: {
        left: insets.l + PAD,
        top: insets.t + HEADER_H,
        width: w - PAD * 2,
        height: stageH,
        radius: 16,
      },
      body: { left: 0, top: HEADER_H + stageH + 6, right: 0, bottom: 0 },
    };
  }, [
    mode, contentW, contentH, insets.l, insets.t, split, stackedByChoice,
    settings, isConnected, isConnecting, avatarHidden,
  ]);

  // ── Host placement ─────────────────────────────────────────────────────────
  // Inside the loader the iframe is already in the right place, so the panel
  // just fills it. Standalone (a preview tab, or a hand-rolled iframe) it has
  // to place itself, and it uses the same rules the loader applies so the
  // preview is honest.

  const hostStyle = useMemo(() => {
    const base = { position: 'absolute', inset: 0 };
    if (usesLoader || alwaysOpen) return base;

    const gap = 20;
    const bottom = settings.position.startsWith('top') ? null : gap;
    const top = settings.position.startsWith('top') ? gap : null;
    const horizontal = settings.position.endsWith('left') ? { left: gap } : { right: gap };
    const corner = { position: 'fixed', ...(top !== null ? { top } : { bottom }), ...horizontal };

    // The card is placed from an explicit left/top rather than a corner: once it
    // has been dragged it is no longer anchored to anything.
    if (mode === 'pip') {
      const pos = pipPos || {
        left: settings.position.endsWith('left')
          ? gap
          : Math.max(gap, hostViewport.vw - pipSize.width - gap),
        top: settings.position.startsWith('top')
          ? gap
          : Math.max(gap, hostViewport.vh - pipSize.height - gap),
      };
      return {
        position: 'fixed',
        left: pos.left,
        top: pos.top,
        width: pipSize.width,
        height: pipSize.height,
      };
    }

    if (mode !== 'open') return corner;

    if (isSpotlight) {
      // The same rect the loader would give it, so a preview tab or a hand
      // rolled iframe is honest about size, scale mode and anchor alike.
      const rect = avatarSpotlightRect(settings, hostViewport.vw, hostViewport.vh);
      return {
        position: 'fixed',
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height,
      };
    }
    const flush = settings.drawerEdge === 'flush';
    const inset = flush ? 0 : 20;
    return {
      position: 'fixed',
      top: inset,
      bottom: inset,
      [settings.side === 'left' ? 'left' : 'right']: inset,
      width: `min(${settings.panelWidth}px, calc(100vw - ${inset * 2}px))`,
    };
  }, [usesLoader, alwaysOpen, mode, isSpotlight, settings, pipPos, pipSize, hostViewport]);

  // ── Render helpers ─────────────────────────────────────────────────────────

  const statusLabel = isConnected
    ? (settings.showTimer ? `Live · ${formatClock(elapsed)}` : 'Live')
    : isConnecting ? 'Connecting' : settings.subtitle;
  // The header does not repeat the call state. The stage carries a Live badge
  // with the timer a few pixels below it, so saying it twice just crowds the
  // agent's name. Connecting is the exception: there is no badge on the stage
  // until the first frame arrives, so the header is the only thing that can say
  // anything at all. (The mini pill still uses `statusLabel` - it has no badge to
  // defer to, only a 52px circle.)
  const headerLabel = isConnecting ? 'Connecting' : settings.subtitle;

  const launcherIcon = { chat: MessageSquare, mic: Mic, sparkles: Sparkles }[settings.launcherIcon] || Sparkles;
  const sizeConf = LAUNCHER_SIZES[settings.launcherSize] || LAUNCHER_SIZES.medium;

  // Which sides are clear of the viewport edge. Only a loader-hosted iframe can
  // ever be flush against one; a standalone panel floats and is round all over.
  const clear = {
    l: !usesLoader || insets.l > 0,
    t: !usesLoader || insets.t > 0,
    r: !usesLoader || insets.r > 0,
    b: !usesLoader || insets.b > 0,
  };
  // A corner stays round while either of the two sides meeting there is clear.
  // That is what turns a flush drawer into a panel rounded only on its inner
  // edge, and a phone drawer into a bottom sheet rounded only along the top.
  const cornerR = viewMode === 'full' ? 0 : theme.radius;
  const cornerRadius = {
    borderTopLeftRadius: clear.l || clear.t ? cornerR : 0,
    borderTopRightRadius: clear.t || clear.r ? cornerR : 0,
    borderBottomRightRadius: clear.r || clear.b ? cornerR : 0,
    borderBottomLeftRadius: clear.b || clear.l ? cornerR : 0,
  };

  // A wide stacked band is a horizontal slice of a 16:9 frame, and a centred
  // slice takes the top of the head with it. Past about 2.2:1 the crop is severe
  // enough to lose the face, so it is biased upward - where the head is in every
  // talking-head avatar there is. Narrower bands, the split column and the mini
  // pill are all centred as before.
  const stageAspect = geometry.stage
    ? geometry.stage.width / Math.max(1, geometry.stage.height)
    : 1;
  const stageObjectPosition = stackedByChoice && mode === 'open' && stageAspect > 2.2
    ? 'center 32%'
    : 'center';

  const stageStyle = !geometry.stage ? null : {
    position: 'absolute',
    left: geometry.stage.left,
    top: geometry.stage.top,
    width: geometry.stage.width,
    height: geometry.stage.height,
    borderRadius: geometry.stage.radius,
    overflow: 'hidden',
    background: theme.isLight ? mix(theme.surface, '#000000', 0.06) : '#05080f',
    display: mode === 'closed' ? 'none' : 'block',
    transition: 'left .38s cubic-bezier(.22,.61,.36,1), top .38s cubic-bezier(.22,.61,.36,1), width .38s cubic-bezier(.22,.61,.36,1), height .38s cubic-bezier(.22,.61,.36,1), border-radius .38s cubic-bezier(.22,.61,.36,1)',
    boxShadow: mode === 'mini'
      ? `0 0 0 2px ${rgba(theme.accent, 0.35)}`
      // In PIP the stage is the whole widget, so it carries the border and the
      // shadow the panel shell would normally give it. Both stay inside the
      // loader's 20px gutter, which is what keeps the shadow from being clipped
      // into a grey edge by the iframe.
      : mode === 'pip' ? `${theme.shadow}, 0 0 0 1px ${rgba(theme.border, 0.9)}` : 'none',
    zIndex: 3,
    ...(mode === 'pip' ? {
      // Dragging the card to a screen edge squeezes the loader's shadow gutter,
      // which moves the stage inside the frame. Easing that would make the card
      // lag its own cursor, so the morph transition is off for the drag only.
      ...(pipDragging ? { transition: 'none' } : null),
      cursor: pipDragging ? 'grabbing' : 'grab',
      // Without this the browser claims the gesture as a pan and cancels the
      // pointer stream mid-drag on touch.
      touchAction: 'none',
      userSelect: 'none',
      WebkitUserSelect: 'none',
    } : null),
  };

  // ── States ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="bx-ap" style={{ ...hostStyle, display: 'grid', placeItems: 'center', background: 'transparent' }}>
        <Loader2 className="bx-ap-spin" size={22} color={theme.accent} />
      </div>
    );
  }

  if (error && !agent) {
    return (
      <div className="bx-ap" style={{ ...hostStyle, fontFamily: theme.font }}>
        <div
          style={{
            position: 'absolute',
            left: insets.l,
            top: insets.t,
            right: insets.r,
            bottom: insets.b,
            display: 'grid',
            placeItems: 'center',
            padding: 20,
            background: theme.bg,
            color: theme.danger,
            border: `1px solid ${theme.border}`,
            boxShadow: theme.shadow,
            ...cornerRadius,
            fontSize: 13,
            textAlign: 'center',
          }}
        >
          {error}
        </div>
      </div>
    );
  }

  // ── Launcher ───────────────────────────────────────────────────────────────

  const renderLauncher = () => {
    const isCircle = settings.launcherStyle === 'circle' || narrowViewport;
    const alignRight = !settings.position.endsWith('left');
    return (
      <div
        style={{
          display: 'inline-flex',
          flexDirection: 'column',
          alignItems: alignRight ? 'flex-end' : 'flex-start',
          gap: 10,
        }}
      >
        {teaserVisible && settings.teaserMessage && (
          <div
            className="bx-ap-pop"
            style={{
              position: 'relative',
              maxWidth: 250,
              background: theme.bg,
              color: theme.text,
              border: `1px solid ${theme.border}`,
              borderRadius: 16,
              padding: '11px 30px 11px 14px',
              fontSize: 13,
              lineHeight: 1.45,
              whiteSpace: 'normal',
              boxShadow: theme.shadow,
            }}
          >
            {settings.teaserMessage}
            <button
              type="button"
              onClick={dismissTeaser}
              aria-label="Dismiss"
              className="bx-ap-btn bx-ap-ghost"
              style={{
                position: 'absolute',
                top: 6,
                right: 6,
                width: 20,
                height: 20,
                borderRadius: 6,
                border: 'none',
                background: 'transparent',
                color: theme.faint,
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <X size={12} />
            </button>
          </div>
        )}

        <button
          type="button"
          onClick={open}
          aria-label={settings.launcherLabel || 'Open the assistant'}
          className="bx-ap-btn"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 10,
            height: isCircle ? sizeConf.dot : sizeConf.pillH,
            width: isCircle ? sizeConf.dot : 'auto',
            padding: isCircle ? 0 : '0 18px 0 6px',
            justifyContent: 'center',
            borderRadius: 999,
            border: 'none',
            background: theme.launcher,
            color: theme.launcherText,
            fontSize: sizeConf.font,
            fontWeight: 600,
            letterSpacing: '-0.01em',
            boxShadow: `0 5px 14px ${rgba(theme.launcher, 0.36)}`,
            animation: settings.pulse ? 'bx-ap-pulse 2.6s ease-out infinite' : 'none',
          }}
        >
          {/* The poster is a still of the avatar, so with the avatar hidden the
              launcher falls back to the client's own logo and, failing that,
              the icon. */}
          {settings.showLauncherPreview && (settings.logoUrl || (!avatarHidden && settings.posterUrl)) ? (
            <img
              src={avatarHidden ? settings.logoUrl : (settings.posterUrl || settings.logoUrl)}
              alt=""
              style={{
                width: isCircle ? '100%' : sizeConf.logo + 8,
                height: isCircle ? '100%' : sizeConf.logo + 8,
                borderRadius: '50%',
                objectFit: 'cover',
                flexShrink: 0,
                border: `2px solid ${rgba(theme.launcherText, 0.35)}`,
              }}
            />
          ) : (
            <span
              style={{
                width: isCircle ? '100%' : sizeConf.logo + 8,
                height: isCircle ? '100%' : sizeConf.logo + 8,
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                background: isCircle ? 'transparent' : rgba(theme.launcherText, 0.16),
                flexShrink: 0,
              }}
            >
              {React.createElement(launcherIcon, { size: isCircle ? 22 : 18 })}
            </span>
          )}
          {!isCircle && (
            <span style={{ whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 7 }}>
              {settings.launcherLabel}
              <LiveDot color={rgba(theme.launcherText, 0.85)} size={6} />
            </span>
          )}
        </button>
      </div>
    );
  };

  // ── Mini pill ──────────────────────────────────────────────────────────────

  const renderMini = () => (
    <div
      style={{
        position: 'relative',
        width: 272,
        height: 68,
        borderRadius: 34,
        background: theme.bg,
        border: `1px solid ${theme.border}`,
        boxShadow: theme.shadow,
        display: 'flex',
        alignItems: 'center',
        paddingLeft: 70,
        paddingRight: 10,
        gap: 6,
        fontFamily: theme.font,
        overflow: 'hidden',
      }}
    >
      {/* With an avatar, the 52px circle on the left of this pill is the stage,
          which is a sibling of the pill and morphs into it. With no avatar
          nothing is coming to fill that space, so the brand mark holds it and
          the pill keeps its shape. */}
      {avatarHidden && (
        <div
          style={{
            position: 'absolute',
            left: 9,
            top: 9,
            width: 50,
            height: 50,
            borderRadius: 25,
            overflow: 'hidden',
            display: 'grid',
            placeItems: 'center',
            background: theme.surface,
            border: `1px solid ${theme.hairline}`,
          }}
        >
          <BrandMark logoUrl={settings.logoUrl} size={50} theme={theme} icon={launcherIcon} />
        </div>
      )}
      <button
        type="button"
        onClick={open}
        className="bx-ap-btn"
        style={{
          flex: 1,
          minWidth: 0,
          textAlign: 'left',
          background: 'transparent',
          border: 'none',
          padding: 0,
          color: theme.text,
        }}
      >
        <div style={{ fontSize: 13, fontWeight: 650, letterSpacing: '-0.01em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {settings.title}
        </div>
        <div style={{ fontSize: 11, color: theme.muted, display: 'flex', alignItems: 'center', gap: 5, marginTop: 2 }}>
          <LiveDot color={theme.live} size={6} />
          {statusLabel}
        </div>
      </button>

      {settings.showMicToggle && (
        <IconButton
          onClick={toggleMic}
          label={micEnabled ? 'Mute microphone' : 'Unmute microphone'}
          theme={theme}
          tone={micEnabled ? 'ghost' : 'danger'}
          size={32}
        >
          {micEnabled ? <Mic size={15} /> : <MicOff size={15} />}
        </IconButton>
      )}
      <IconButton onClick={close} label="End conversation" theme={theme} tone="danger" size={32}>
        <PhoneOff size={15} />
      </IconButton>
    </div>
  );

  // ── Panel chrome ───────────────────────────────────────────────────────────

  const renderHeader = () => (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: HEADER_H,
        display: 'flex',
        alignItems: 'center',
        gap: 11,
        padding: '0 12px 0 16px',
        borderBottom: `1px solid ${theme.hairline}`,
        zIndex: 4,
      }}
    >
      <BrandMark logoUrl={settings.logoUrl} size={34} theme={theme} icon={launcherIcon} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div
          style={{
            fontSize: 14.5,
            fontWeight: 650,
            letterSpacing: '-0.015em',
            color: theme.text,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {settings.title}
        </div>
        <div style={{ fontSize: 11.5, color: theme.muted, display: 'flex', alignItems: 'center', gap: 5, marginTop: 1 }}>
          {isConnecting && <LiveDot color={theme.faint} size={6} />}
          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{headerLabel}</span>
        </div>
      </div>

      {historyAvailable && (
        <IconButton
          onClick={() => (pane === 'live' ? openHistory() : setPane('live'))}
          label={pane === 'live' ? (settings.historyLabel || 'History') : 'Back to the conversation'}
          theme={theme}
          tone={pane === 'live' ? 'ghost' : 'solid'}
          size={32}
        >
          <Clock size={16} />
        </IconButton>
      )}
      {isConnected && (
        <IconButton onClick={stopSession} label="End conversation" theme={theme} tone="danger" size={32}>
          <PhoneOff size={15} />
        </IconButton>
      )}
      {!alwaysOpen && settings.minimizeEnabled && (
        <IconButton onClick={minimize} label="Minimize" theme={theme} size={32}>
          <ChevronDown size={17} />
        </IconButton>
      )}
      {!alwaysOpen && (
        <IconButton onClick={close} label="Close" theme={theme} size={32}>
          <X size={16} />
        </IconButton>
      )}
    </div>
  );

  const renderStage = () => (
    <div
      style={stageStyle}
      className="bx-ap-stage"
      {...(mode === 'pip'
        ? {
          onPointerDown: onPipPointerDown,
          onPointerMove: onPipPointerMove,
          // Wrapped: these hand the event in as the first argument, which is not
          // what `endPipDrag` takes.
          onPointerUp: () => endPipDrag(),
          onPointerCancel: () => endPipDrag(),
          onDoubleClick: exitPip,
          role: 'group',
          'aria-label': 'Live assistant video. Drag to move, double click to expand.',
        }
        : null)}
    >
      {/* The video element is mounted once for the life of the widget. Moving
          it between the panel band and the mini circle by geometry alone is
          what keeps the session running through a minimize. */}
      <video
        ref={videoRef}
        autoPlay
        playsInline
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: stageObjectPosition,
          display: avatarVideoTrack ? 'block' : 'none',
          background: '#05080f',
        }}
        onLoadedMetadata={() => videoRef.current?.play().catch(() => {})}
        onCanPlay={() => {
          videoRef.current?.play().catch(() => {});
          if (!avatarReadyNotifiedRef.current && room?.localParticipant) {
            avatarReadyNotifiedRef.current = true;
            try {
              const msg = new TextEncoder().encode(JSON.stringify({ type: 'avatar_video_ready' }));
              room.localParticipant.publishData(msg, { reliable: true, topic: 'avatar_status' });
            } catch { /* the agent copes without the hint */ }
          }
        }}
      />

      {/* Idle poster / connecting state */}
      {!avatarVideoTrack && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'grid',
            placeItems: 'center',
            backgroundImage: settings.posterUrl ? `url(${settings.posterUrl})` : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center top',
          }}
        >
          {!settings.posterUrl && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                background: `radial-gradient(120% 90% at 50% 0%, ${rgba(theme.accent, 0.28)} 0%, transparent 62%), ${theme.isLight ? mix(theme.surface, '#000', 0.04) : '#080d17'}`,
              }}
            />
          )}
          {isConnecting ? (
            <ConnectingStage theme={theme} compact={mode === 'mini'} logoUrl={settings.logoUrl} />
          ) : (
            mode !== 'mini' && !settings.posterUrl && (
              <div style={{ position: 'relative', textAlign: 'center', opacity: 0.85 }}>
                <BrandMark logoUrl={settings.logoUrl} size={52} theme={theme} icon={launcherIcon} />
              </div>
            )
          )}
        </div>
      )}

      {/* Speaking ring. --bx-level is written straight to the node by the
          reveal loop, so this animates without a re-render. */}
      <div
        ref={stageRingRef}
        style={{
          position: 'absolute',
          inset: 0,
          borderRadius: 'inherit',
          pointerEvents: 'none',
          boxShadow: `inset 0 0 0 calc(1px + var(--bx-level, 0) * 3px) ${rgba(theme.accent, 0.75)}`,
          opacity: isConnected ? 1 : 0,
          transition: 'opacity .3s ease',
        }}
      />

      {/* PIP chrome. Everything that is not a control is a drag handle, so the
          bar stays as small as it can be and fades out while the visitor is not
          pointing at the card - a parked PIP window is just the avatar. */}
      {mode === 'pip' && (
        <>
          <div
            style={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              background: 'linear-gradient(to bottom, rgba(0,0,0,.44) 0%, transparent 32%, transparent 66%, rgba(0,0,0,.44) 100%)',
              opacity: avatarVideoTrack ? 1 : 0,
              transition: 'opacity .3s ease',
            }}
          />

          {isConnected && (
            <div
              style={{
                position: 'absolute',
                top: 8,
                left: 9,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                padding: '3px 8px',
                borderRadius: 999,
                background: 'rgba(8,12,20,.5)',
                backdropFilter: 'blur(6px)',
                color: '#fff',
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                pointerEvents: 'none',
              }}
            >
              <LiveDot color={theme.live} size={5} />
              Live
              {settings.showTimer && (
                <span style={{ opacity: 0.72, fontVariantNumeric: 'tabular-nums', letterSpacing: 0 }}>
                  {formatClock(elapsed)}
                </span>
              )}
            </div>
          )}

          <div
            className="bx-ap-pip-bar"
            style={{ position: 'absolute', top: 7, right: 7, display: 'flex', gap: 5, zIndex: 2 }}
          >
            {settings.showMicToggle && (
              <PipButton
                onClick={toggleMic}
                label={micEnabled ? 'Mute microphone' : 'Unmute microphone'}
                danger={!micEnabled}
              >
                {micEnabled ? <Mic size={14} /> : <MicOff size={14} />}
              </PipButton>
            )}
            <PipButton onClick={exitPip} label="Back to the panel">
              <Maximize2 size={14} />
            </PipButton>
            <PipButton onClick={close} label="End conversation" danger>
              <PhoneOff size={13} />
            </PipButton>
          </div>

          {/* The transcript is not on screen in PIP, so a client running
              subtitles keeps them here rather than losing the words entirely. */}
          {settings.captionStyle === 'subtitles' && isConnected && captions.length > 0 && (
            <div
              style={{
                position: 'absolute',
                left: 8,
                right: 8,
                bottom: 8,
                padding: '5px 9px',
                borderRadius: 10,
                background: 'rgba(8,12,20,.62)',
                backdropFilter: 'blur(8px)',
                color: '#fff',
                fontSize: 11.5,
                lineHeight: 1.4,
                maxHeight: 48,
                overflow: 'hidden',
                pointerEvents: 'none',
              }}
            >
              {captions[captions.length - 1].text}
            </div>
          )}
        </>
      )}

      {mode === 'open' && (
        <>
          <div
            style={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              background: 'linear-gradient(to bottom, rgba(0,0,0,.34) 0%, transparent 26%, transparent 62%, rgba(0,0,0,.42) 100%)',
              opacity: avatarVideoTrack ? 1 : 0,
              transition: 'opacity .3s ease',
            }}
          />
          {/* Pop out. It lives on the video rather than in the header for the
              same reason a player puts it there: the thing it detaches is the
              thing you are looking at, and the header is already carrying five
              controls on a 420px drawer. Always visible, never on hover - a
              control nobody knows is there is a control nobody uses. */}
          {pipAvailable && hasSession && (
            <div style={{ position: 'absolute', top: 8, right: 8, zIndex: 2 }}>
              <PipButton onClick={enterPip} label="Pop out the video">
                <PictureInPicture2 size={14} />
              </PipButton>
            </div>
          )}

          {isConnected && (
            <div
              style={{
                position: 'absolute',
                top: 10,
                left: 12,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '4px 9px',
                borderRadius: 999,
                background: 'rgba(8,12,20,.55)',
                backdropFilter: 'blur(6px)',
                color: '#fff',
                fontSize: 10.5,
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
              }}
            >
              <LiveDot color={theme.live} size={6} />
              Live
              {settings.showTimer && (
                <span style={{ opacity: 0.72, fontVariantNumeric: 'tabular-nums', letterSpacing: 0 }}>
                  {formatClock(elapsed)}
                </span>
              )}
            </div>
          )}
          {/* Subtitle overlay for setups that hide the transcript */}
          {settings.captionStyle === 'subtitles' && isConnected && captions.length > 0 && (
            <div
              style={{
                position: 'absolute',
                left: 12,
                right: 12,
                bottom: 12,
                padding: '8px 12px',
                borderRadius: 12,
                background: 'rgba(8,12,20,.62)',
                backdropFilter: 'blur(8px)',
                color: '#fff',
                fontSize: 13,
                lineHeight: 1.45,
                maxHeight: 76,
                overflow: 'hidden',
              }}
            >
              {captions[captions.length - 1].text}
            </div>
          )}
        </>
      )}
    </div>
  );

  const renderIdle = () => {
    // The stock headline promises a face. With the avatar hidden that is a lie,
    // so the default - and only the default, never a headline the client wrote -
    // is swapped for one that describes what they actually turned on.
    const headline = avatarHidden && settings.headline === AVATAR_PANEL_DEFAULTS.headline
      ? 'Ask us anything'
      : settings.headline;
    return (
    <div
      className="bx-ap-enter"
      style={{
        position: 'absolute',
        ...geometry.body,
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
        padding: split ? '18px 20px 16px' : '16px 18px 14px',
        gap: 12,
      }}
    >
      <div style={{ flex: 1, minHeight: 0 }}>
        <h2
          style={{
            margin: 0,
            fontSize: split ? 24 : 19,
            lineHeight: 1.2,
            letterSpacing: '-0.025em',
            fontWeight: 700,
            color: theme.text,
          }}
        >
          {headline}
        </h2>
        {settings.description && (
          <p style={{ margin: '8px 0 0', fontSize: 13.5, lineHeight: 1.55, color: theme.muted }}>
            {settings.description}
          </p>
        )}

        {dynamicVariableNames.length > 0 && (
          <div style={{ display: 'grid', gap: 8, marginTop: 14 }}>
            {dynamicVariableNames.map((name) => (
              <input
                key={name}
                type="text"
                value={dynamicVariableValues[name] || ''}
                onChange={(e) => setDynamicVariableValues((prev) => ({ ...prev, [name]: e.target.value }))}
                placeholder={name.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: 11,
                  border: `1px solid ${theme.border}`,
                  background: theme.surface,
                  color: theme.text,
                  fontSize: 13.5,
                  outline: 'none',
                  fontFamily: 'inherit',
                }}
              />
            ))}
          </div>
        )}

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginTop: 14 }}>
          {[
            { icon: Volume2, label: 'Speak or type' },
            { icon: Sparkles, label: 'Answers in real time' },
          ].map(({ icon: Icon, label }) => (
            <span
              key={label}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '5px 10px',
                borderRadius: 999,
                background: theme.surface,
                border: `1px solid ${theme.hairline}`,
                color: theme.muted,
                fontSize: 11.5,
                fontWeight: 550,
              }}
            >
              <Icon size={12} />
              {label}
            </span>
          ))}
        </div>
      </div>

      <div style={{ display: 'grid', gap: 9 }}>
        <button
          type="button"
          onClick={startSession}
          disabled={isConnecting || !!error}
          className="bx-ap-btn"
          style={{
            position: 'relative',
            overflow: 'hidden',
            width: '100%',
            height: 50,
            borderRadius: 14,
            border: 'none',
            background: theme.accent,
            color: theme.accentText,
            fontSize: 15,
            fontWeight: 650,
            letterSpacing: '-0.01em',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 9,
            opacity: isConnecting || error ? 0.6 : 1,
            boxShadow: `0 6px 18px ${rgba(theme.accent, 0.3)}`,
          }}
        >
          {isConnecting ? <Loader2 className="bx-ap-spin" size={18} /> : <Mic size={18} />}
          {isConnecting ? 'Connecting…' : settings.startLabel}
          {!isConnecting && !error && (
            <span
              aria-hidden
              style={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                width: 60,
                background: `linear-gradient(90deg, transparent, ${rgba(theme.accentText, 0.22)}, transparent)`,
                animation: 'bx-ap-sheen 3.6s ease-in-out infinite',
              }}
            />
          )}
        </button>
        {error && <p style={{ margin: 0, fontSize: 12, color: theme.danger }}>{error}</p>}
        {settings.consentText && (
          <p style={{ margin: 0, fontSize: 11, lineHeight: 1.5, color: theme.faint, textAlign: 'center' }}>
            {settings.consentText}
          </p>
        )}
      </div>
    </div>
    );
  };

  // ── History panes ──────────────────────────────────────────────────────────
  // Both occupy the same box as the transcript, under the stage. The avatar and
  // any running session are untouched: a visitor can read back an old
  // conversation mid-call and the current one carries on behind them.

  const renderHistoryList = () => (
    <div
      className="bx-ap-enter"
      style={{
        position: 'absolute',
        ...geometry.body,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          padding: '12px 16px 8px',
          flexShrink: 0,
        }}
      >
        <div>
          <div style={{ fontSize: 13.5, fontWeight: 650, color: theme.text, letterSpacing: '-0.01em' }}>
            {settings.historyLabel || 'History'}
          </div>
          <div style={{ fontSize: 11, color: theme.faint, marginTop: 1 }}>
            Your conversations from the last 24 hours
          </div>
        </div>
        {historyList.length > 0 && (
          <button
            type="button"
            onClick={clearHistory}
            className="bx-ap-btn bx-ap-ghost"
            style={{
              border: 'none',
              background: 'transparent',
              color: theme.faint,
              fontSize: 11.5,
              padding: '5px 8px',
              borderRadius: 8,
              flexShrink: 0,
            }}
          >
            Clear all
          </button>
        )}
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '0 14px 14px' }}>
        {historyLoading ? (
          <div style={{ display: 'grid', placeItems: 'center', padding: '28px 0' }}>
            <Loader2 className="bx-ap-spin" size={18} color={theme.faint} />
          </div>
        ) : historyList.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '26px 10px', color: theme.faint, fontSize: 12.5, lineHeight: 1.55 }}>
            Nothing here yet. Conversations you have on this site show up here for
            24 hours, and you can pick any of them back up.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {historyList.map((entry) => (
              <div
                key={entry.id}
                style={{
                  display: 'flex',
                  alignItems: 'stretch',
                  gap: 4,
                  background: theme.surface,
                  border: `1px solid ${theme.hairline}`,
                  borderRadius: 13,
                  overflow: 'hidden',
                }}
              >
                <button
                  type="button"
                  onClick={() => openHistoryDetail(entry.id)}
                  disabled={historyBusy}
                  className="bx-ap-btn"
                  style={{
                    flex: 1,
                    minWidth: 0,
                    textAlign: 'left',
                    border: 'none',
                    background: 'transparent',
                    color: theme.text,
                    padding: '11px 4px 11px 13px',
                  }}
                >
                  <div
                    style={{
                      fontSize: 13,
                      lineHeight: 1.45,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      wordBreak: 'break-word',
                    }}
                  >
                    {entry.preview || 'Conversation'}
                  </div>
                  <div style={{ fontSize: 10.5, color: theme.faint, marginTop: 4, display: 'flex', gap: 7 }}>
                    <span>{relativeTime(entry.last_activity_at)}</span>
                    <span>·</span>
                    <span>{entry.turn_count} {entry.turn_count === 1 ? 'message' : 'messages'}</span>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => deleteHistoryEntry(entry.id)}
                  aria-label="Delete this conversation"
                  title="Delete this conversation"
                  className="bx-ap-btn bx-ap-ghost"
                  style={{
                    border: 'none',
                    background: 'transparent',
                    color: theme.faint,
                    width: 38,
                    flexShrink: 0,
                    display: 'grid',
                    placeItems: 'center',
                  }}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderHistoryDetail = () => {
    const turns = historyDetail?.turns || [];
    return (
      <div
        className="bx-ap-enter"
        style={{
          position: 'absolute',
          ...geometry.body,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '10px 14px 8px',
            flexShrink: 0,
          }}
        >
          <IconButton onClick={() => setPane('history')} label="Back to history" theme={theme} size={30}>
            <ArrowLeft size={15} />
          </IconButton>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 12.5, fontWeight: 600, color: theme.text }}>
              {relativeTime(historyDetail?.last_activity_at)}
            </div>
            <div style={{ fontSize: 10.5, color: theme.faint }}>
              {turns.length} {turns.length === 1 ? 'message' : 'messages'}
            </div>
          </div>
        </div>

        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '4px 16px 10px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
            {turns.map((turn, index) => {
              const isUser = turn.speaker === 'user';
              return (
                <div
                  key={`${historyDetail.id}-${index}`}
                  style={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start', gap: 8 }}
                >
                  {!isUser && (
                    <BrandMark logoUrl={settings.logoUrl} size={26} theme={theme} icon={launcherIcon} />
                  )}
                  <div
                    style={{
                      maxWidth: '82%',
                      padding: '9px 13px',
                      fontSize: 13.5,
                      lineHeight: 1.5,
                      borderRadius: 16,
                      borderBottomRightRadius: isUser ? 5 : 16,
                      borderBottomLeftRadius: isUser ? 16 : 5,
                      // Read-only, so it is drawn a step back from the live
                      // transcript rather than in full accent.
                      background: isUser ? theme.accentSoft : theme.surface,
                      color: theme.text,
                      border: `1px solid ${theme.hairline}`,
                      wordBreak: 'break-word',
                    }}
                  >
                    {turn.text}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div style={{ padding: '8px 14px 12px', borderTop: `1px solid ${theme.hairline}`, flexShrink: 0 }}>
          <button
            type="button"
            onClick={() => resumeConversation(historyDetail)}
            disabled={historyBusy || isConnecting}
            className="bx-ap-btn"
            style={{
              width: '100%',
              height: 46,
              borderRadius: 13,
              border: 'none',
              background: theme.accent,
              color: theme.accentText,
              fontSize: 14.5,
              fontWeight: 650,
              letterSpacing: '-0.01em',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              opacity: historyBusy || isConnecting ? 0.6 : 1,
              boxShadow: `0 5px 14px ${rgba(theme.accent, 0.28)}`,
            }}
          >
            {historyBusy || isConnecting
              ? <Loader2 className="bx-ap-spin" size={16} />
              : <RotateCcw size={16} />}
            Continue this conversation
          </button>
          <p style={{ margin: '7px 0 0', fontSize: 10.5, lineHeight: 1.5, color: theme.faint, textAlign: 'center' }}>
            The assistant picks up with everything you talked about here.
          </p>
        </div>
      </div>
    );
  };

  const renderTranscript = () => {
    return (
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '14px 16px 6px' }}>
        {captions.length === 0 && !agentThinking && (
          <div style={{ textAlign: 'center', padding: '18px 6px', color: theme.faint, fontSize: 12.5 }}>
            {isConnecting
              // With no stage there is no ConnectingStage either, so this line is
              // the only thing telling the visitor anything is happening.
              ? 'Connecting…'
              : 'You are connected. Start talking, or type below.'}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
          {captions.map((c, index) => {
            const isUser = c.speaker === 'user';
            // Where the resumed thread ends and the new conversation starts.
            // Without a marker the visitor cannot tell which of these words were
            // just spoken and which were read back out of storage.
            const resumeBoundary = !c.earlier && index > 0 && captions[index - 1].earlier;
            return (
              <React.Fragment key={c.key}>
              {resumeBoundary && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 9, margin: '3px 0' }}>
                  <span style={{ flex: 1, height: 1, background: theme.hairline }} />
                  <span style={{ fontSize: 10, color: theme.faint, letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 700 }}>
                    Continued
                  </span>
                  <span style={{ flex: 1, height: 1, background: theme.hairline }} />
                </div>
              )}
              <div
                className="bx-ap-enter"
                style={{
                  display: 'flex',
                  justifyContent: isUser ? 'flex-end' : 'flex-start',
                  gap: 8,
                  // Earlier turns are context, not part of what is being said
                  // now, and read that way.
                  opacity: c.earlier ? 0.72 : 1,
                }}
              >
                {!isUser && (
                  <BrandMark logoUrl={settings.logoUrl} size={26} theme={theme} icon={launcherIcon} />
                )}
                <div
                  style={{
                    maxWidth: '82%',
                    padding: '9px 13px',
                    fontSize: 13.5,
                    lineHeight: 1.5,
                    borderRadius: 16,
                    borderBottomRightRadius: isUser ? 5 : 16,
                    borderBottomLeftRadius: isUser ? 16 : 5,
                    background: isUser ? theme.accent : theme.surface,
                    color: isUser ? theme.accentText : theme.text,
                    border: isUser ? 'none' : `1px solid ${theme.hairline}`,
                    wordBreak: 'break-word',
                  }}
                >
                  {c.images?.length ? (
                    <>
                      <ChatMessageImages images={c.images} />
                      {stripImageMarker(c.text)}
                    </>
                  ) : c.text}
                </div>
              </div>
              </React.Fragment>
            );
          })}

          {agentThinking && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <BrandMark logoUrl={settings.logoUrl} size={26} theme={theme} icon={launcherIcon} />
              <div
                style={{
                  padding: '11px 14px',
                  borderRadius: 16,
                  borderBottomLeftRadius: 5,
                  background: theme.surface,
                  border: `1px solid ${theme.hairline}`,
                }}
              >
                <TypingDots color={theme.faint} />
              </div>
            </div>
          )}
        </div>

        <div ref={transcriptEndRef} style={{ height: 1 }} />
      </div>
    );
  };

  const renderComposer = () => (
    <div style={{ padding: '8px 14px 12px', borderTop: `1px solid ${theme.hairline}`, flexShrink: 0 }}>
      {cobrowse.active && isConnected && cobrowse.audioBlocked && (
        <button
          type="button"
          onClick={cobrowse.resumeAudio}
          className="bx-ap-btn"
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            margin: '0 0 8px',
            padding: '9px 12px',
            borderRadius: 11,
            border: 'none',
            background: theme.accent,
            color: theme.accentText,
            fontWeight: 600,
            fontSize: 13,
            fontFamily: 'inherit',
          }}
        >
          <Volume2 size={15} /> Tap to hear the assistant
        </button>
      )}
      {cobrowse.active && isConnected && cobrowse.controlStopped && (
        <p style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '0 0 7px', fontSize: 11.5, color: theme.muted }}>
          <MousePointerClick size={13} /> Page control is off. The assistant will describe where to click.
        </p>
      )}
      {error && (
        <p style={{ margin: '0 0 7px', fontSize: 11.5, color: theme.danger }}>{error}</p>
      )}
      {imagesEnabled && settings.showTextInput && (
        <ChatAttachmentStrip attachments={attachments} isDarkMode={!theme.isLight} accentColor={theme.accent} />
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {settings.showMicToggle && (
          <button
            type="button"
            ref={micRingRef}
            onClick={toggleMic}
            aria-label={micEnabled ? 'Mute microphone' : 'Unmute microphone'}
            title={micEnabled ? 'Mute microphone' : 'Unmute microphone'}
            className="bx-ap-btn"
            style={{
              width: 42,
              height: 42,
              borderRadius: 13,
              border: 'none',
              flexShrink: 0,
              display: 'grid',
              placeItems: 'center',
              background: micEnabled ? theme.surface : theme.dangerSoft,
              color: micEnabled ? theme.text : theme.danger,
              boxShadow: micEnabled
                ? `0 0 0 calc(var(--bx-level, 0) * 5px) ${rgba(theme.accent, 0.28)}`
                : 'none',
              transition: 'box-shadow .09s linear, background-color .18s ease',
            }}
          >
            {micEnabled ? <Mic size={17} /> : <MicOff size={17} />}
          </button>
        )}

        {settings.showTextInput ? (
          <div
            style={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              height: 42,
              padding: '0 6px 0 14px',
              borderRadius: 13,
              background: theme.surface,
              border: `1px solid ${theme.hairline}`,
            }}
          >
            {imagesEnabled && (
              <>
                <input
                  ref={imageInputRef}
                  type="file"
                  accept={CHAT_IMAGE_ACCEPT}
                  multiple
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    attachments.addFiles(e.target.files);
                    e.target.value = '';
                  }}
                />
                <button
                  type="button"
                  onClick={() => imageInputRef.current?.click()}
                  aria-label="Attach image"
                  title="Attach image"
                  className="bx-ap-btn"
                  style={{
                    width: 28,
                    height: 28,
                    marginLeft: -8,
                    borderRadius: 9,
                    border: 'none',
                    display: 'grid',
                    placeItems: 'center',
                    flexShrink: 0,
                    background: 'transparent',
                    color: theme.muted,
                  }}
                >
                  <ImagePlus size={16} />
                </button>
              </>
            )}
            <input
              type="text"
              value={textValue}
              onChange={(e) => setTextValue(e.target.value)}
              onPaste={(e) => {
                if (!imagesEnabled) return;
                const files = imageFilesFrom(e.clipboardData);
                if (files.length) {
                  e.preventDefault();
                  attachments.addFiles(files);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendText();
                }
              }}
              placeholder={settings.inputPlaceholder}
              style={{
                flex: 1,
                minWidth: 0,
                border: 'none',
                outline: 'none',
                background: 'transparent',
                color: theme.text,
                fontSize: 13.5,
                fontFamily: 'inherit',
              }}
            />
            <button
              type="button"
              onClick={() => sendText()}
              disabled={(!textValue.trim() && !(imagesEnabled && attachments.readyCount > 0)) || attachments.uploading || sending}
              aria-label="Send message"
              className="bx-ap-btn"
              style={{
                width: 32,
                height: 32,
                borderRadius: 10,
                border: 'none',
                display: 'grid',
                placeItems: 'center',
                flexShrink: 0,
                background: textValue.trim() || (imagesEnabled && attachments.readyCount > 0) ? theme.accent : 'transparent',
                color: textValue.trim() || (imagesEnabled && attachments.readyCount > 0) ? theme.accentText : theme.faint,
                opacity: sending ? 0.5 : 1,
              }}
            >
              <Send size={15} />
            </button>
          </div>
        ) : (
          <div style={{ flex: 1, fontSize: 12.5, color: theme.faint, paddingLeft: 4 }}>
            {micEnabled ? 'Listening…' : 'Microphone is muted'}
          </div>
        )}
      </div>

      {settings.poweredBy && (
        <div style={{ textAlign: 'center', marginTop: 8, fontSize: 10, color: theme.faint, letterSpacing: '0.02em' }}>
          Powered by BrainCX
        </div>
      )}
    </div>
  );

  const renderPanel = () => (
    <div
      style={{
        position: 'absolute',
        left: insets.l,
        top: insets.t,
        right: insets.r,
        bottom: insets.b,
        background: theme.bg,
        color: theme.text,
        ...cornerRadius,
        border: viewMode === 'full' ? 'none' : `1px solid ${theme.border}`,
        boxShadow: viewMode === 'full' ? 'none' : theme.shadow,
        overflow: 'hidden',
        fontFamily: theme.font,
        zIndex: 2,
      }}
    >
      {renderHeader()}
      {/* History replaces the body only. The stage is a sibling of this chrome,
          so a live avatar keeps playing above the list and the session is never
          interrupted by someone reading back an old conversation. */}
      {pane === 'history' ? (
        renderHistoryList()
      ) : pane === 'historyDetail' ? (
        renderHistoryDetail()
      ) : isConnected || isConnecting ? (
        <div
          style={{
            position: 'absolute',
            ...geometry.body,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
          {...(isConnected ? imageDrop.dropHandlers : {})}
        >
          <ChatDropOverlay active={imageDrop.dragActive} accentColor={theme.accent} isDarkMode={!theme.isLight} />
          {/* With an avatar, the stage carries the connecting state. With none,
              this is the only place it can go - and a line of faint grey text is
              not a loading screen. Skipped once there are captions to show: a
              resumed conversation arrives with its history already seeded, and
              that should not be hidden behind a loader.
              Subtitles are an overlay on the video, so with the avatar hidden
              there is nothing to overlay and the words go back to the transcript
              rather than nowhere. */}
          {avatarHidden && isConnecting && captions.length === 0 ? (
            <ConnectingBody theme={theme} logoUrl={settings.logoUrl} icon={launcherIcon} />
          ) : settings.captionStyle === 'transcript' || avatarHidden ? (
            renderTranscript()
          ) : (
            <div style={{ flex: 1 }} />
          )}
          {renderComposer()}
        </div>
      ) : (
        renderIdle()
      )}
    </div>
  );

  // ── Compose ────────────────────────────────────────────────────────────────

  return (
    <div
      ref={hostRef}
      className="bx-ap"
      style={{
        ...hostStyle,
        fontFamily: theme.font,
        WebkitFontSmoothing: 'antialiased',
        zIndex: 2147483000,
        // Nothing may escape the widget box. During the frame between "launcher
        // rendered" and "loader resized the iframe to match", the launcher is
        // larger than the frame; clipping that is right, growing a scrollbar
        // over it is not. The measurement is unaffected - the wrapper is sized
        // max-content, so being clipped does not change what it reports.
        overflow: 'hidden',
      }}
    >
      <style dangerouslySetInnerHTML={{ __html: styleSheet(theme) }} />

      {(mode === 'mini' || (mode === 'closed' && !alwaysOpen)) && (
        <div
          ref={setLauncherNode}
          // Measured unconstrained, on purpose. The loader derives the iframe
          // width from this measurement, so measuring inside a box that width
          // already produced feeds back into itself: the pill would shrink the
          // frame, the narrow frame would wrap or clip the pill, and it would
          // settle on the wrong size. `max-content` asks for the intrinsic
          // width instead, and `nowrap` keeps the label on one line even if a
          // browser ignores it.
          style={{
            position: 'absolute',
            left: insets.l,
            top: insets.t,
            display: 'block',
            width: 'max-content',
            whiteSpace: 'nowrap',
          }}
        >
          {mode === 'mini' ? renderMini() : renderLauncher()}
        </div>
      )}
      {mode === 'open' && renderPanel()}
      {mode !== 'closed' && !avatarHidden && renderStage()}

      <audio ref={audioRef} autoPlay />
    </div>
  );
};

export default AvatarPanelV2;
